import { useEffect, useMemo, useState, useRef } from 'react';
import {
  apiInvestigados,
  apiPesquisasRegistradas,
  QUALIFICACOES,
  type AtivoDoInvestigado,
  type ClassificacaoDoAtivo,
  type ConsolidacaoDeAtivos,
  type EfeitoPatrimonialDoAto,
  type Investigado,
  type CampoLido,
  type MatriculaProposta,
  type ModalidadeDaAvaliacao,
  type OrigemDoCampo,
  type NivelServico,
  type PapelDaParteNoAto,
  type PesquisaRegistrada,
  type PesquisaRegistravel,
  type PlanoDeImoveis,
} from '../lib/api';
import { usarEhMaster } from '../lib/papel';
import { filaDe, marcarLancado, pedidoDoCodexDaFila, tirar, type ItemDaFila } from '../lib/fila-de-matriculas';
import { avaliarLeitura, ContaDasLeituras, contarLeituras, FichaComMatricula, propostaDaLeitura, proximaLeitura, type LancadoNoCaso } from './MatriculaNaFicha';
import { avisar, avisarErro } from './Avisos';
import { Confirmar } from './Confirmar';
import {
  BuscarImoveis,
  DOCUMENTADA,
  EFEITO_A_CONFERIR,
  EFEITO_TIRA,
  FormularioDeOnus,
  MODALIDADES,
  PEDE_VALOR,
  ROTULO,
  ROTULO_DA_MODALIDADE,
  ROTULO_DO_EFEITO,
  dinheiro,
  emArea,
  type LinhaDeAvaliacao,
} from './AtivosLocalizados';
import { QUALIDADES_NO_ATO, RELACOES, TIPOS_DE_ATO, TIPOS_DE_IMOVEL } from '../lib/listas-do-imovel';
import { cnpjValido, documentoConfere, documentoNaTela, soDigitos } from '../lib/documento';
import { PAPEIS_DA_PARTE, ROTULO_DO_PAPEL_DA_PARTE, documentoComRotulo, fraseDoAto } from '../lib/partes-do-imovel';
import { EFEITOS_DO_ATO } from '../lib/efeito-do-ato';
import { OpcoesAgrupadas } from './OpcoesAgrupadas';
import { AnaliseDoProcesso } from './AtivosLocalizados';
import '../estilos/imoveis-2026.css';
import { completarReais, digitarReais } from '../lib/dinheiro.ts';

/**
 * ⭐⭐ **O TÓPICO IMÓVEIS, REPAGINADO — 16/09/2026.**
 *
 * > *"Repagine essa aba ou tópico do imóveis, pois atualmente ela é poluída.
 * > Atue como web design especialista… se ficar perfeito, iremos replicar o
 * > mesmo modelo de formulário para todos os formulários existentes no HUB."*
 * > — Richard
 *
 * **Antes:** o formulário inteiro aberto para CADA parte, com explicação sob
 * cada campo — 5.208 px com três partes. **Agora:** resumo no alto, uma parte
 * por vez, três botões, uma linha por imóvel que abre o detalhe, e o
 * formulário **passo a passo** (a proposta B que ele escolheu) só quando se
 * pede. Desenho em `docs/sistema-visual-2026/imoveis-16-09/`.
 *
 * ⛔ **Muda aparência e lugar, nunca o que o botão faz.** Ler PDF, busca
 * automática, registro de pesquisa, gravames, aceite, corrigir e apagar são as
 * mesmas peças de antes — importadas, não copiadas.
 *
 * ⚠️ **A avaliação NÃO passa pelo sigilo de valores** (é o patrimônio do
 * investigado, não o preço da KAIRÓS), como em `AtivosLocalizados`.
 */

/** As pesquisas feitas fora do Hub que moram neste tópico (decisão 14 + 14/09). */
export type PesquisaDoTopico = { servico: PesquisaRegistravel; rotulo: string; achou: string };
const PESQUISAS_DO_IMOVEL: PesquisaDoTopico[] = [
  { servico: 'imoveis_por_titular', rotulo: 'Cartórios por titular', achou: 'Achou imóvel' },
  { servico: 'indisponibilidade_de_bens', rotulo: 'Indisponibilidade de bens', achou: 'Achou ordem' },
];

const DEFINICAO: Partial<Record<ClassificacaoDoAtivo, string>> = {
  imovel_livre:
    'Consta em nome do investigado e não tem ônus ativo identificado na matrícula. Entra na soma do patrimônio.',
  imovel_onerado:
    'Segue, total ou em parte, em nome do investigado, com ônus ou gravame ativo. Entra na soma, em separado do livre.',
  imovel_transmitido:
    'Foi do investigado e saiu do nome dele. Entra como histórico — não soma ao patrimônio atual.',
  imovel_matricula_encerrada:
    'A matrícula foi encerrada (unificação, desmembramento…). O imóvel segue em outra matrícula.',
  imovel_de_terceiro:
    'Nunca foi do investigado. Aparece por um fato objetivo que o liga a ele — e esse fato é obrigatório.',
  /* ⭐ 16/10/2026 (PRD §51). Uma opção, duas respostas: quem decide é a titular. */
  imovel_livre_empresa_do_grupo:
    'Livre, em nome de empresa do grupo. Se a empresa é parte do caso, soma como livre na ficha dela. Se não é, só demonstra o vínculo e não soma.',
  imovel_de_empresa_do_grupo:
    'Livre, em nome de empresa do grupo. Se a empresa é parte do caso, soma como livre na ficha dela. Se não é, só demonstra o vínculo e não soma.',
};

/** As duas faces de "Livre, empresa do grupo": a tela mostra uma opção só. */
const EMPRESA_DO_GRUPO: ClassificacaoDoAtivo[] = ['imovel_livre_empresa_do_grupo', 'imovel_de_empresa_do_grupo'];

const CLASSES: ClassificacaoDoAtivo[] = [
  'imovel_livre',
  'imovel_livre_empresa_do_grupo',
  'imovel_onerado',
  'imovel_transmitido',
  'imovel_matricula_encerrada',
  'imovel_de_terceiro',
];

const CLASSE_CSS: Partial<Record<ClassificacaoDoAtivo, string>> = {
  imovel_livre: 'livre',
  /* ⚠️ Sem cor nova: o livre da empresa é livre; o de empresa que não é parte
     tem o fio do terceiro, que é o que não se afirma. */
  imovel_livre_empresa_do_grupo: 'livre',
  imovel_onerado: 'onerado',
  imovel_transmitido: 'transmitido',
  imovel_matricula_encerrada: 'encerrada',
  imovel_de_terceiro: 'terceiro',
  imovel_de_empresa_do_grupo: 'terceiro',
};

/** O nome curto da situação, para selo e escolha: "Livre", "Em nome de terceiro". */
const CURTO: Partial<Record<ClassificacaoDoAtivo, string>> = {
  imovel_livre: 'Livre',
  imovel_livre_empresa_do_grupo: 'Livre, empresa do grupo',
  imovel_onerado: 'Onerado',
  imovel_transmitido: 'Transmitido',
  imovel_matricula_encerrada: 'Matrícula encerrada',
  imovel_de_terceiro: 'Em nome de terceiro',
  imovel_de_empresa_do_grupo: 'Empresa do grupo',
};

const COR_DO_NIVEL: Record<NivelServico, string> = {
  n1_insight: 'var(--nivel-insight)',
  n2_forensic: 'var(--nivel-forensic)',
  n3_kairos: 'var(--nivel-kairos)',
  customizado: 'var(--purpura)',
};

const emData = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '');
export const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter((p) => p.length > 2 || /^[A-ZÀ-Ú]$/.test(p))
    .slice(0, 2)
    .map((p) => p[0])
    .join('');

/** "MATRÍCULA 12.345 · 2º RI DE X" → número e cartório. Nulo se não reconhecer. */
function separarIdentificacao(t: string | null | undefined): { numero: string; cartorio: string } | null {
  if (!t) return null;
  const m = t.match(/MATR[ÍI]CULA\s*(?:N[º°.]?\s*)?([\d.\-/]+)\s*(?:[·—–-]\s*)?(.*)$/i);
  return m ? { numero: m[1]!.trim(), cartorio: m[2]!.trim() } : null;
}

type Dados = {
  ativos: AtivoDoInvestigado[];
  consolidacao: ConsolidacaoDeAtivos | null;
};

export function ImoveisDoCaso({
  casoId,
  nivel,
  chave,
  aoMudarPesquisa,
}: {
  casoId: string;
  nivel: NivelServico;
  chave: string;
  /** Avisa a página para recarregar a ressalva do capítulo. */
  aoMudarPesquisa?: () => void;
}) {
  const ehMaster = usarEhMaster();
  const [partes, setPartes] = useState<Investigado[] | null>(null);
  const [porParte, setPorParte] = useState<Record<string, Dados>>({});
  const [registradas, setRegistradas] = useState<PesquisaRegistrada[]>([]);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [revisao, setRevisao] = useState(0);
  /* ⭐ §76: as duas pesquisas (a busca automática e o registro) num painel só. */
  const [painel, setPainel] = useState<null | 'pesquisas'>(null);
  /*
   * O formulário: fechado, lançando (com ou sem proposta) ou corrigindo. `daFila`: a leitura da fila que está nele.
   * ⭐ §77: `completarCom`, a leitura de uma matrícula já lançada nesta parte, que completa só o vazio do lançamento.
   */
  const [ficha, setFicha] = useState<null | { corrigindo?: AtivoDoInvestigado; proposta?: MatriculaProposta; daFila?: string; completarCom?: MatriculaProposta }>(null);
  /* ⭐ §76: a ficha nova já recebeu algo digitado? Em branco, a leitura pronta abre nela sozinha; com algo digitado,
     a leitura espera a vez (⛔ nenhum botão apaga o que foi digitado). */
  const [alterada, setAlterada] = useState(false);

  useEffect(() => {
    let vivo = true;
    void Promise.all([apiInvestigados.listar(casoId), apiPesquisasRegistradas.doCaso(casoId)])
      .then(([i, r]) => {
        if (!vivo) return;
        setPartes(i.investigados);
        setRegistradas(r.registradas);
        setEscolhida((atual) => atual ?? i.investigados[0]?.id ?? null);
      })
      .catch(() => vivo && setPartes([]));
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  useEffect(() => {
    if (!partes) return;
    let vivo = true;
    void Promise.all(
      partes.map((p) =>
        apiInvestigados
          .ativos(p.id)
          .then(
            (r) =>
              [
                p.id,
                {
                  ativos: r.ativos.filter((a) => a.categoria === 'imoveis'),
                  /* ⭐ 18/09/2026: os gravames da legenda são só os dos imóveis — antes
                     vinham da parte inteira, e o veículo alienado aparecia aqui. */
                  consolidacao: r.porCategoria ? { ...r.consolidacao, ...r.porCategoria.imoveis } : r.consolidacao,
                },
              ] as const,
          )
          .catch(() => [p.id, { ativos: [], consolidacao: null }] as const),
      ),
    ).then((pares) => {
      if (vivo) setPorParte(Object.fromEntries(pares));
    });
    return () => {
      vivo = false;
    };
  }, [partes, revisao]);

  if (partes === null) return <p className="ajuda">Carregando…</p>;
  if (partes.length === 0) {
    return (
      <p className="ajuda ajuda-solta">
        Este caso ainda não tem parte investigada cadastrada. Os imóveis se lançam por pessoa —
        cadastre os investigados no caso primeiro.
      </p>
    );
  }

  const parte = partes.find((p) => p.id === escolhida) ?? partes[0]!;
  const dados = porParte[parte.id];
  const todos = Object.values(porParte).flatMap((d) => d.ativos);
  const aoMudar = () => setRevisao((n) => n + 1);
  /* ⭐ §70: o que já está lançado no caso, por parte, para a fila dizer "já lançada" e "em outra parte". */
  const lancados: LancadoNoCaso[] = Object.entries(porParte).flatMap(([parteId, d]) =>
    d.ativos.map((a) => ({ ativoId: a.id, parteId, numero: a.numeroDaMatricula, cartorio: a.cartorio, cidade: a.cidade, uf: a.uf })),
  );

  function mudarFicha(f: typeof ficha) {
    setPainel(null);
    setAlterada(false);
    setFicha(f);
  }

  function abrirFicha(f: { corrigindo?: AtivoDoInvestigado; proposta?: MatriculaProposta; daFila?: string }) {
    /* ⛔ Nenhum botão apaga o que o caso já tem: com uma ficha aberta e algo digitado, nada abre por cima. */
    if (ficha && alterada && (f.proposta || f.corrigindo)) {
      avisar('Há um formulário aberto, com o que foi digitado. Lance ou cancele antes; nada foi descartado.');
      return;
    }
    mudarFicha(f);
  }

  /**
   * ⭐ §77: abre uma leitura na ficha. A matrícula já lançada nesta parte abre como "completar o lançamento" (só o
   * vazio); a que não foi lida, em branco, com o motivo no alto.
   */
  function abrirLeitura(item: ItemDaFila) {
    const a = avaliarLeitura(item, filaDe(parte.id), lancados, parte.id);
    const proposta = propostaDaLeitura(item) ?? undefined;
    const ativo = a.situacao === 'ja-lancada' && a.lancamento ? dados?.ativos.find((x) => x.id === a.lancamento) : undefined;
    if (ativo && proposta) mudarFicha({ corrigindo: ativo, completarCom: proposta, daFila: item.id });
    else mudarFicha({ proposta, daFila: item.id });
  }

  /**
   * ⭐ §76 e §77: depois de lançar ou descartar (e no "+ Imóvel" com leituras esperando): a próxima leitura abre na
   * ficha; se ainda há leitura em andamento, abre a ficha em branco, que se preenche sozinha; senão, a ficha fecha.
   */
  function seguir(exceto?: string) {
    const itens = filaDe(parte.id);
    const proxima = proximaLeitura(itens, lancados, parte.id, exceto);
    if (proxima) abrirLeitura(proxima);
    else if (itens.some((i) => i.estado === 'lendo' || i.estado === 'na-fila')) mudarFicha({});
    else mudarFicha(null);
  }

  return (
    <div className="imv" style={{ ['--imv-nivel' as string]: COR_DO_NIVEL[nivel] }}>
      <Resumo ativos={todos} />

      <div className="imv-pessoas" role="tablist" aria-label="Parte investigada">
        {partes.map((p) => {
          const n = porParte[p.id]?.ativos.length ?? 0;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === parte.id}
              className={p.id === parte.id ? 'ativo' : undefined}
              onClick={() => {
                if (ficha) {
                  avisar('Lance ou cancele o formulário aberto antes de trocar de parte.');
                  return;
                }
                setEscolhida(p.id);
                setPainel(null);
              }}
            >
              <span className="imv-av">{iniciais(p.nome)}</span>
              <span className="imv-txt">
                <b>{p.nome}</b>
                <small>{QUALIFICACOES[p.qualificacao]}</small>
              </span>
              <span className={`imv-conta${n === 0 ? ' zero' : ''}`} title={n === 0 ? 'nada lançado' : undefined}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {ficha ? (
        (() => {
          const daLeitura = ficha.daFila ?? null;
          const aFicha = (bloqueio: string | null, confirmacao: Parameters<typeof FichaDoImovel>[0]['confirmacao']) => (
            <FichaDoImovel
              key={`${ficha.corrigindo?.id ?? ''}${daLeitura ?? ''}${ficha.proposta ? 'proposta' : ''}` || 'novo'}
              investigado={parte}
              doCaso={partes}
              corrigindo={ficha.corrigindo ?? null}
              partindoDe={ficha.proposta ?? null}
              completarCom={ficha.completarCom ?? null}
              bloqueio={bloqueio}
              confirmacao={confirmacao}
              aoAlterar={() => setAlterada(true)}
              aoFechar={() => mudarFicha(null)}
              /* ⭐ §77: na ficha que veio de uma leitura, "Cancelar" vira "Descartar" (a leitura sai, e vem a próxima) */
              aoDescartar={
                daLeitura
                  ? () => {
                      tirar(parte.id, daLeitura);
                      seguir(daLeitura);
                    }
                  : undefined
              }
              aoGravar={(corrigidos) => {
                if (daLeitura) {
                  /* ⭐ PRD §75: só o número de campos mudados à mão, para a volta automática do comitê comparar modelos. */
                  const pedido = pedidoDoCodexDaFila(parte.id, daLeitura);
                  if (pedido && corrigidos !== undefined && !ficha.corrigindo) apiInvestigados.correcoesDaLeitura(parte.id, pedido, corrigidos);
                  marcarLancado(parte.id, daLeitura);
                }
                aoMudar();
                /* ⭐ §76: "Lançar" leva à próxima matrícula lida (a correção à mão de um imóvel já lançado só fecha). */
                if (ficha.corrigindo && !daLeitura) mudarFicha(null);
                else seguir(daLeitura ?? undefined);
              }}
            />
          );
          /* ⭐ §76 e §77: a ficha nova (e a que completa com a leitura) traz a matrícula no alto; só o master lê PDF. */
          return ehMaster && (!ficha.corrigindo || ficha.completarCom) ? (
            <FichaComMatricula
              investigadoId={parte.id}
              nomeDaParte={parte.nome}
              lancados={lancados}
              naFicha={daLeitura}
              completando={!!ficha.completarCom}
              podeAbrir={!daLeitura && !ficha.proposta && !ficha.corrigindo && !alterada}
              aoAbrir={abrirLeitura}
              aoAbrirLancamento={(ativoId) => {
                const ativo = dados?.ativos.find((a) => a.id === ativoId);
                if (ativo) mudarFicha({ corrigindo: ativo });
              }}
            >
              {aFicha}
            </FichaComMatricula>
          ) : (
            aFicha(null, null)
          );
        })()
      ) : (
        <>
          <div className="imv-acoes">
            {/* ⭐⭐ §76 e §77: UM botão. Com matrícula lida esperando, ele abre a próxima; senão, a ficha em branco. */}
            <button
              type="button"
              className="imv-bt primario"
              aria-label="Acrescentar imóvel"
              onClick={() => (ehMaster && contarLeituras(filaDe(parte.id)).esperam > 0 ? seguir() : abrirFicha({}))}
            >
              <Traco d={['M12 5v14', 'M5 12h14']} />
              Imóvel
              {ehMaster && <ContaDasLeituras investigadoId={parte.id} />}
            </button>
          </div>

          <FaixaDePesquisas
            investigado={parte}
            registradas={registradas}
            painel={painel}
            ehMaster={ehMaster}
            aoAbrir={setPainel}
          />

          {/* ⭐ §76: as duas pesquisas num painel só: a busca automática (paga, do master, com o preço) e o registro. */}
          {painel === 'pesquisas' && (
            <>
              {ehMaster && (
                <div className="imv-painel">
                  <BuscarImoveis investigadoId={parte.id} chave={String(revisao)} />
                </div>
              )}
              <RegistroDePesquisa
                investigado={parte}
                registradas={registradas}
                ehMaster={ehMaster}
                aoMudar={(novas) => {
                  setRegistradas(novas);
                  aoMudarPesquisa?.();
                }}
              />
            </>
          )}

          <ListaDeImoveis
            dados={dados}
            investigadoId={parte.id}
            ehMaster={ehMaster}
            aoMudar={aoMudar}
            aoCorrigir={(a) => abrirFicha({ corrigindo: a })}
          />
        </>
      )}
    </div>
  );
}

/* ── Ícone em traço, como `Icone.tsx` ─────────────────────────────────────── */
export function Traco({ d }: { d: string[] }) {
  return (
    <svg className="imv-ic" viewBox="0 0 24 24" aria-hidden="true">
      {d.map((x) => (
        <path key={x} d={x} />
      ))}
    </svg>
  );
}

/* ── O resumo do caso ─────────────────────────────────────────────────────── */
function Resumo({ ativos }: { ativos: AtivoDoInvestigado[] }) {
  const de = (c: ClassificacaoDoAtivo) => ativos.filter((a) => a.classificacao === c);
  const soma = (lista: AtivoDoInvestigado[]) => {
    const com = lista.filter((a) => a.valorAtribuivel !== null);
    return com.length === 0 ? null : com.reduce((s, a) => s + (a.valorAtribuivel ?? 0), 0);
  };
  /* ⭐ 16/10/2026: o livre da empresa do grupo que é parte do caso soma como livre. */
  const livres = [...de('imovel_livre'), ...de('imovel_livre_empresa_do_grupo')];
  const onerados = de('imovel_onerado');
  const gravames = onerados.reduce(
    (n, a) => n + a.onus.filter((o) => (o.situacao ?? '').trim().toUpperCase() === 'ATIVO').length,
    0,
  );
  const fora = ativos.filter((a) => !a.entraNaConsolidacao).length;
  const semValor = ativos.filter((a) => PEDE_VALOR.includes(a.classificacao) && a.valorAtribuivel === null).length;
  const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
  const somaLivres = soma(livres);
  const somaOnerados = soma(onerados);
  return (
    <div className="imv-resumo">
      <div className="imv-kpi k-livre">
        <span className="imv-rot">Livres</span>
        <strong>{somaLivres === null ? '—' : dinheiro(somaLivres)}</strong>
        <small>{plural(livres.length, 'imóvel no patrimônio', 'imóveis no patrimônio')}</small>
      </div>
      <div className="imv-kpi k-onus">
        <span className="imv-rot">Onerados</span>
        <strong>{somaOnerados === null ? '—' : dinheiro(somaOnerados)}</strong>
        <small>
          {plural(onerados.length, 'imóvel', 'imóveis')} · {plural(gravames, 'gravame ativo', 'gravames ativos')}
        </small>
      </div>
      <div className="imv-kpi k-fora">
        <span className="imv-rot">Fora do atual</span>
        <strong>{fora}</strong>
        <small>não soma ao patrimônio</small>
      </div>
      <div className="imv-kpi k-sem">
        <span className="imv-rot">Sem avaliação</span>
        <strong>{semValor}</strong>
        <small>não apurado — não é zero</small>
      </div>
    </div>
  );
}

/* ── A faixa das pesquisas desta parte ────────────────────────────────────── */
function FaixaDePesquisas({
  investigado,
  registradas,
  painel,
  ehMaster,
  aoAbrir,
}: {
  investigado: Investigado;
  registradas: PesquisaRegistrada[];
  painel: null | 'pesquisas';
  ehMaster: boolean;
  aoAbrir: (p: null | 'pesquisas') => void;
}) {
  const [plano, setPlano] = useState<PlanoDeImoveis | null>(null);
  useEffect(() => {
    let vivo = true;
    setPlano(null);
    /* ⛔ **O plano da busca é do master — 19/09/2026** (a rota é `SO_MASTER`:
       ele traz o custo da busca). A faixa pedia para todos, e o servidor
       recusava o colaborador em silêncio a cada abertura. Para ele, a faixa
       mostra os registros, como já mostrava; o selo da busca é do master. */
    if (!ehMaster) return;
    apiInvestigados
      .planoDeImoveis(investigado.id)
      .then((r) => vivo && setPlano(r.plano))
      .catch(() => vivo && setPlano(null));
    return () => {
      vivo = false;
    };
  }, [investigado.id, painel, ehMaster]);

  const respondidos = plano?.alvos.filter((a) => a.ultima && a.ultima.falha === null) ?? [];
  const ultima = respondidos
    .map((a) => a.ultima!.em)
    .sort()
    .at(-1);
  return (
    <div className="imv-fontes">
      <span className="imv-rot">Pesquisado para esta parte</span>
      {PESQUISAS_DO_IMOVEL.map((p) => {
        const r = registradas.find((x) => x.investigadoId === investigado.id && x.servico === p.servico);
        return (
          <span key={p.servico} className={`imv-fonte${r ? ' ok' : ' falta'}`}>
            <i />
            {p.rotulo} ·{' '}
            {r
              ? `${r.achou ? p.achou.toLowerCase() : 'nada consta'} · ${new Date(r.em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`
              : 'não registrada'}
          </span>
        );
      })}
      {plano && plano.alvos.length > 0 && (
        <span className={`imv-fonte${respondidos.length > 0 ? ' ok' : ' falta'}`}>
          <i />
          Busca automática ·{' '}
          {respondidos.length === 0
            ? 'ainda não'
            : `${respondidos.length} de ${plano.alvos.length} documento${plano.alvos.length === 1 ? '' : 's'} · ${new Date(ultima!).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`}
        </span>
      )}
      <span className="imv-esp" />
      {/* ⭐ §76: a busca automática e o registro num botão só; a faixa fica sendo a lista do que já se pesquisou. */}
      <button
        type="button"
        className={`imv-bt fantasma p${painel === 'pesquisas' ? ' aberto' : ''}`}
        aria-expanded={painel === 'pesquisas'}
        onClick={() => aoAbrir(painel === 'pesquisas' ? null : 'pesquisas')}
      >
        <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
        Pesquisas desta parte
      </button>
    </div>
  );
}

/**
 * ⭐ **"Registrei esta pesquisa", dentro do tópico — 16/09/2026.** Era um
 * bloco com todas as partes, solto abaixo do tópico; agora é da parte
 * escolhida, ao lado das pesquisas dela. ⚠️ A regra é a de `PesquisaFeitaFora`:
 * registrar de novo substitui, e só o master registra.
 */
export function RegistroDePesquisa({
  investigado,
  registradas,
  ehMaster,
  aoMudar,
  pesquisas = PESQUISAS_DO_IMOVEL,
}: {
  investigado: Investigado;
  registradas: PesquisaRegistrada[];
  ehMaster: boolean;
  aoMudar: (novas: PesquisaRegistrada[]) => void;
  /** ⭐ 18/09/2026: as pesquisas do tópico — Imóveis por padrão; Outros ativos passa as marcas. */
  pesquisas?: PesquisaDoTopico[];
}) {
  const [indo, setIndo] = useState<string | null>(null);
  const [aApagar, setAApagar] = useState<{ registro: PesquisaRegistrada; rotulo: string } | null>(null);

  async function registrar(servico: PesquisaRegistravel, achou: boolean) {
    setIndo(servico);
    try {
      const { registrada } = await apiPesquisasRegistradas.registrar(investigado.id, servico, achou);
      aoMudar([
        ...registradas.filter((r) => !(r.investigadoId === investigado.id && r.servico === servico)),
        registrada,
      ]);
    } catch (e) {
      avisarErro(e);
    } finally {
      setIndo(null);
    }
  }

  async function apagar(r: PesquisaRegistrada) {
    setIndo(r.servico);
    try {
      await apiPesquisasRegistradas.apagar(r.investigadoId, r.id);
      aoMudar(registradas.filter((x) => x.id !== r.id));
    } catch (e) {
      avisarErro(e);
    } finally {
      setIndo(null);
    }
  }

  return (
    <div className="imv-painel">
      <p className="imv-painel-titulo">Pesquisas feitas fora do Hub · {investigado.nome}</p>
      <p className="ajuda">
        É o registro que faz o escopo citar a pesquisa e a ressalva sair no relatório — sem ele, o
        capítulo vazio parece dizer que ninguém olhou. O que se achou continua sendo lançado abaixo.
      </p>
      <ul className="imv-registros">
        {pesquisas.map((p) => {
          const r = registradas.find((x) => x.investigadoId === investigado.id && x.servico === p.servico) ?? null;
          return (
            <li key={p.servico}>
              <span className="imv-registro-nome">{p.rotulo}</span>
              <span className={`imv-registro-situacao${r ? ' feito' : ''}`}>
                {r
                  ? `${r.achou ? p.achou : 'Nada consta'} · registrado em ${new Date(r.em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`
                  : 'Ainda não registrada'}
              </span>
              {ehMaster && (
                <span className="imv-registro-acoes">
                  <button
                    type="button"
                    className="imv-bt p"
                    aria-pressed={r !== null && !r.achou}
                    disabled={indo === p.servico || (r !== null && !r.achou)}
                    onClick={() => void registrar(p.servico, false)}
                  >
                    Nada consta
                  </button>
                  <button
                    type="button"
                    className="imv-bt p"
                    aria-pressed={r !== null && r.achou}
                    disabled={indo === p.servico || (r !== null && r.achou)}
                    onClick={() => void registrar(p.servico, true)}
                  >
                    {p.achou}
                  </button>
                  {r && (
                    <button type="button" className="imv-bt p fantasma" disabled={indo === p.servico} onClick={() => setAApagar({ registro: r, rotulo: p.rotulo })}>
                      Apagar registro
                    </button>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {/* ⭐ **Pergunta antes — 19/09/2026**, como em `PesquisaFeitaFora`. */}
      {aApagar && (
        <Confirmar
          titulo="Apagar este registro?"
          descricao={`A pesquisa "${aApagar.rotulo}" volta a "ainda não registrada" para ${investigado.nome}, e o relatório deixa de contá-la como feita para essa parte. O que foi lançado no caso continua.`}
          rotuloAcao="Apagar o registro"
          aoConfirmar={() => void apagar(aApagar.registro)}
          aoFechar={() => setAApagar(null)}
        />
      )}
    </div>
  );
}

/* ── A lista ──────────────────────────────────────────────────────────────── */
function ListaDeImoveis({
  dados,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  dados: Dados | undefined;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aberto, setAberto] = useState<string | null>(null);
  if (!dados) return <p className="ajuda">Carregando…</p>;
  const c = dados.consolidacao;
  return (
    <>
      <div className="imv-lista">
        <div className="imv-grade imv-cab">
          <span className="imv-rot">Situação</span>
          <span className="imv-rot">Bem</span>
          <span className="imv-rot">Último ato</span>
          <span className="imv-rot dir">Valor na conta</span>
          <span />
        </div>
        {dados.ativos.length === 0 ? (
          /* ⚠️ Vazio diz "nada lançado" e NÃO some: ausência de bem não é
             ausência de pesquisa. */
          <p className="imv-vazio">Nada lançado para esta parte.</p>
        ) : (
          dados.ativos.map((a) => (
            <LinhaDoImovel
              key={a.id}
              ativo={a}
              aberto={aberto === a.id}
              aoAbrir={() => setAberto(aberto === a.id ? null : a.id)}
              investigadoId={investigadoId}
              ehMaster={ehMaster}
              aoMudar={aoMudar}
              aoCorrigir={aoCorrigir}
            />
          ))
        )}
      </div>
      <div className="imv-legenda">
        <span>
          <b>—</b> valor não apurado: o bem conta na lista e fica fora da soma
        </span>
        {c && c.exposicaoQuantos > 0 && (
          <span>
            <span className="imv-info">i</span> Gravames ativos: {c.exposicaoQuantos}
            {c.exposicao > 0 && <> · somam {dinheiro(c.exposicao)}</>}
            {c.exposicaoSemValor > 0 && <> · {c.exposicaoSemValor} sem valor no papel</>} — valor do ato, não
            saldo devedor; <strong>não se subtraem</strong> do patrimônio
          </span>
        )}
        {c && c.gravamesSemSituacao > 0 && (
          <span>
            ⚠️ {c.gravamesSemSituacao} gravame(s) com <strong>situação não apurada</strong> — não somam e não se
            ignoram
          </span>
        )}
      </div>
    </>
  );
}

function LinhaDoImovel({
  ativo,
  aberto,
  aoAbrir,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  ativo: AtivoDoInvestigado;
  aberto: boolean;
  aoAbrir: () => void;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aApagar, setAApagar] = useState(false);
  /* ⛔ Apagar um gravame pergunta antes — a regra dele, 18/09/2026. */
  const [gravameAApagar, setGravameAApagar] = useState<AtivoDoInvestigado['onus'][number] | null>(null);
  const gravamesAtivos = ativo.onus.filter((o) => (o.situacao ?? '').trim().toUpperCase() === 'ATIVO');
  const temFiduciaria = gravamesAtivos.some((o) => /aliena[cç][aã]o\s+fiduci|fiduci[aá]ri/i.test(o.natureza));
  const credoresRepetidos = [
    ...new Set(
      gravamesAtivos
        .map((o) => o.credor?.trim().toUpperCase())
        .filter((x): x is string => Boolean(x))
        .filter((x, _i, todos) => todos.filter((y) => y === x).length > 1),
    ),
  ];
  const diasDaCertidao = ativo.certidaoEm
    ? Math.floor((Date.now() - new Date(`${ativo.certidaoEm}T00:00:00.000Z`).getTime()) / 86_400_000)
    : null;
  const areas = [
    ativo.areaPrivativa !== null ? `${emArea(ativo.areaPrivativa)} m² privativos` : null,
    ativo.areaConstruida !== null ? `${emArea(ativo.areaConstruida)} m² construídos` : null,
    ativo.areaDoTerreno !== null ? `${emArea(ativo.areaDoTerreno)} m² de terreno` : null,
  ].filter(Boolean);
  const meta = [ativo.tipoDoImovel, [ativo.cidade, ativo.uf].filter(Boolean).join('/') || null, areas[0] ?? null]
    .filter(Boolean)
    .join(' · ');
  const matricula = ativo.numeroDaMatricula
    ? `MAT. ${ativo.numeroDaMatricula}${ativo.cartorio ? ` · ${ativo.cartorio}` : ''}`
    : ativo.identificacao;
  const outra = ativo.outraParte ?? ativo.adquirente ?? ativo.transmitente;
  const ato = [
    emData(ativo.ocorridoEm) || null,
    ativo.qualidadeNoAto?.toLocaleLowerCase('pt-BR') ?? null,
    outra ? `${ativo.qualidadeNoAto ? 'com' : 'parte:'} ${outra}${ativo.vinculoEntrePartes ? ` (${ativo.vinculoEntrePartes.toLocaleLowerCase('pt-BR')})` : ''}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const naConta = ativo.avaliacoes?.find((x) => x.entraNaConta) ?? null;

  return (
    <article className={`imv-linha${aberto ? ' aberta' : ''}`}>
      <button type="button" className="imv-grade imv-linha-topo" onClick={aoAbrir} aria-expanded={aberto}>
        <span className="imv-sit-coluna">
          <span className={`imv-sit ${CLASSE_CSS[ativo.classificacao] ?? ''}`}>
            <i />
            {CURTO[ativo.classificacao] ?? ROTULO[ativo.classificacao]}
          </span>
          {gravamesAtivos.length > 0 && (
            <span className="imv-tag">
              {gravamesAtivos.length} gravame{gravamesAtivos.length === 1 ? '' : 's'} ativo{gravamesAtivos.length === 1 ? '' : 's'}
            </span>
          )}
          {!ativo.entraNaConsolidacao && <span className="imv-tag cinza">fora do patrimônio atual</span>}
        </span>
        <span className="imv-bem">
          <b>{ativo.descricao}</b>
          {matricula && <span className="imv-mono">{matricula}</span>}
          {meta && <small>{meta}</small>}
        </span>
        <span className="imv-ato">
          <b>{ativo.modalidadeDeAquisicao ?? '—'}</b>
          {ato && <small>{ato}</small>}
        </span>
        <span className="imv-valor">
          {ativo.valorAtribuivel !== null ? (
            <>
              <b>{dinheiro(ativo.valorAtribuivel)}</b>
              <small>
                {ativo.modalidadeDaAvaliacao ? ROTULO_DA_MODALIDADE[ativo.modalidadeDaAvaliacao].toLocaleLowerCase('pt-BR') : 'procedência não declarada'}
                {ativo.percentual !== null && ativo.percentual !== 100 ? ` · ${ativo.percentual}%` : ''}
                {!ativo.entraNaConsolidacao ? ' · não soma' : ''}
              </small>
            </>
          ) : (
            <>
              <b className="na">—</b>
              <small>não apurado</small>
            </>
          )}
        </span>
        <span className="imv-chev" aria-hidden="true">
          <Traco d={[aberto ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6']} />
        </span>
      </button>

      {aberto && (
        <div className="imv-detalhe">
          <div>
            <h5 className="imv-rot">Avaliações</h5>
            {(ativo.avaliacoes ?? []).length === 0 ? (
              <p className="imv-nada">Sem avaliação — o bem conta sem somar dinheiro. Não é zero.</p>
            ) : (
              <ul className="imv-mini">
                {ativo.avaliacoes.map((x) => (
                  <li key={x.id} className={x.entraNaConta ? 'conta' : undefined}>
                    <b>{dinheiro(x.valor)}</b>
                    <span>
                      {x.modalidade ? ROTULO_DA_MODALIDADE[x.modalidade] : 'procedência não declarada'}
                      {x.modalidade && !DOCUMENTADA[x.modalidade] && x.modalidade !== 'a_apurar' ? ' (derivada)' : ''}
                      {x.referencia ? ` · ${x.referencia}` : ''}
                      {x.avaliadaEm ? ` · ${emData(x.avaliadaEm)}` : ''}
                    </span>
                    {x.entraNaConta && <em>na conta</em>}
                  </li>
                ))}
              </ul>
            )}
            {naConta === null && ativo.modalidadeDaAvaliacao === 'a_apurar' && (
              <p className="imv-nada">A apurar — não há elementos para avaliar.</p>
            )}
            {ativo.valorDeclarado !== null && (
              <p className="imv-nada">
                Valor do negócio {ativo.valorGlobal ? '(global da operação) ' : ''}
                <b className="imv-mono">{dinheiro(ativo.valorDeclarado)}</b> — não é avaliação.
              </p>
            )}
            {ativo.valorDeclarado === null && ativo.valorGlobal && (
              <p className="imv-nada">Valor global não individualizado.</p>
            )}
          </div>

          <div>
            <h5 className="imv-rot">Gravames</h5>
            {ativo.onus.length === 0 ? (
              <p className="imv-nada">Nenhum lançado.</p>
            ) : (
              <ul className="imv-mini">
                {ativo.onus.map((o) => (
                  <li key={o.id}>
                    <b>{o.valor !== null ? dinheiro(o.valor) : '—'}</b>
                    <span>
                      {o.natureza}
                      {o.credor ? ` · ${o.credor}` : ''}
                      {o.processo ? ` · processo nº ${o.processo}` : ''}
                      {o.constituidoEm ? ` · desde ${emData(o.constituidoEm)}` : ''}
                    </span>
                    <em className={o.situacao ? 'sit' : 'sit na'}>{o.situacao ?? 'não apurada'}</em>
                    {ehMaster && (
                      <button
                        type="button"
                        className="botao-texto"
                        onClick={() => setGravameAApagar(o)}
                      >
                        apagar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {ehMaster && <FormularioDeOnus ativo={ativo} investigadoId={investigadoId} aoLancar={aoMudar} comProcesso />}
          </div>

          <div>
            <h5 className="imv-rot">Registro</h5>
            <ul className="imv-mini">
              <li>
                <span>
                  {ativo.certidaoEm ? (
                    <>Certidão de {emData(ativo.certidaoEm)}{diasDaCertidao !== null && diasDaCertidao > 90 ? ` — ${diasDaCertidao} dias` : ''}</>
                  ) : (
                    'Data da certidão não apurada'
                  )}
                </span>
              </li>
              {ativo.tabelionato && (
                <li>
                  <span>Origem: {ativo.tabelionato}</span>
                </li>
              )}
              {ativo.efeitoPatrimonial && (
                <li>
                  <span>
                    {ROTULO_DO_EFEITO[ativo.efeitoPatrimonial]}
                    {EFEITO_TIRA[ativo.efeitoPatrimonial]
                      ? ' — o bem saiu do patrimônio neste ato'
                      : EFEITO_A_CONFERIR[ativo.efeitoPatrimonial]
                        ? ' — confira o papel do investigado: o bem pode ter saído ou entrado neste ato'
                        : ''}
                  </span>
                </li>
              )}
              {ativo.proprietarioAtual && (
                <li>
                  <span>
                    Hoje em nome de {ativo.proprietarioAtual}
                    {ativo.documentoDoTitular ? ` (${documentoComRotulo(ativo.documentoDoTitular)})` : ''}
                  </span>
                </li>
              )}
              {/* ⭐ 16/10/2026 (PRD §51): as partes do ato e os coproprietários. */}
              {(ativo.partes ?? []).length > 0 && (
                <li>
                  <span>
                    Partes do ato:{' '}
                    {ativo.partes
                      .map(
                        (p) =>
                          `${p.nome}${p.documento ? ` (${documentoComRotulo(p.documento)})` : ''}${p.papel ? `, ${ROTULO_DO_PAPEL_DA_PARTE[p.papel].toLocaleLowerCase('pt-BR')}` : ''}`,
                      )
                      .join('; ')}
                  </span>
                </li>
              )}
              {(ativo.coproprietarios ?? []).length > 0 && (
                <li>
                  <span>
                    Coproprietários:{' '}
                    {ativo.coproprietarios
                      .map(
                        (c) =>
                          `${c.nome}${c.documento ? ` (${documentoComRotulo(c.documento)})` : ''}, ${c.fracao !== null ? `${c.fracao.toLocaleString('pt-BR')}%` : 'fração não informada'}`,
                      )
                      .join('; ')}
                  </span>
                </li>
              )}
              {ativo.matriculaSucessora && (
                <li>
                  <span>
                    Sucessora: {ativo.matriculaSucessora}
                    {ativo.motivoEncerramento ? ` — ${ativo.motivoEncerramento}` : ''}
                  </span>
                </li>
              )}
              {ativo.elementoDeConexao && (
                <li>
                  <span>Conexão: {ativo.elementoDeConexao}</span>
                </li>
              )}
            </ul>
          </div>

          {/* As quatro travas de precisão — as mesmas de sempre. */}
          {(temFiduciaria ||
            credoresRepetidos.length > 0 ||
            (diasDaCertidao !== null && diasDaCertidao > 90) ||
            !ativo.certidaoEm ||
            ativo.avisoDasFracoes ||
            ativo.titularEhParteDoCaso) && (
            <div className="imv-travas">
              {/* ⭐ 16/10/2026: avisos, nunca recusa. A conta não muda. */}
              {ativo.titularEhParteDoCaso && (
                <p>
                  ⚠️ <strong>A empresa titular passou a ser parte do caso.</strong> O lugar deste imóvel é a ficha
                  dela, onde ele soma como livre. Aqui ele continua fora da soma.
                </p>
              )}
              {ativo.avisoDasFracoes && <p>⚠️ {ativo.avisoDasFracoes}</p>}
              {temFiduciaria && (
                <p>
                  ⚠️ <strong>Alienação fiduciária ativa.</strong> A propriedade resolúvel é do credor fiduciário: o
                  investigado é <strong>titular dos direitos aquisitivos</strong> — é o direito que se alcança, não o imóvel.
                </p>
              )}
              {credoresRepetidos.length > 0 && (
                <p>
                  ⚠️ <strong>Pode haver dupla contagem.</strong> Mais de um gravame ativo em favor de{' '}
                  {credoresRepetidos.join(' e ')} — confira antes de somar.
                </p>
              )}
              {diasDaCertidao !== null && diasDaCertidao > 90 && (
                <p>
                  ⚠️ <strong>A certidão tem {diasDaCertidao} dias.</strong> Penhora ou transmissão posteriores não
                  apareceriam.
                </p>
              )}
              {!ativo.certidaoEm && (
                <p>
                  ⚠️ <strong>A data da certidão não foi apurada</strong> — é lacuna, não "está em dia".
                </p>
              )}
            </div>
          )}

          {/* ⭐ 28/10/2026, pedido dele (PRD §72): a "Obs." de cada imóvel — texto livre, sai no relatório abaixo dele. */}
          <div className="imv-detalhe-obs">
            <AnaliseDoProcesso
              ativo={ativo}
              investigadoId={investigadoId}
              aoMudar={aoMudar}
              rotulo="Obs."
              placeholder="Escreva aqui o que achar necessário sobre este imóvel."
              nota="Sai no relatório, abaixo deste imóvel. Grava ao sair do campo. Em branco, não sai nada."
              linhas={3}
            />
          </div>

          {ativo.interpretacao && (
            <div className={`imv-interpretacao${ativo.aceita ? ' aceita' : ''}`}>
              <p>{ativo.interpretacao}</p>
              <p className="ajuda">
                {ativo.aceita
                  ? 'Aceita — entra no documento entregue.'
                  : 'Escrita pelo Hub, ainda não revista. Não entra no documento entregue.'}
                {ehMaster && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="botao-texto"
                      onClick={() =>
                        void (ativo.aceita
                          ? apiInvestigados.desfazerAceite(investigadoId, ativo.id)
                          : apiInvestigados.aceitarAtivo(investigadoId, ativo.id)
                        )
                          .then(() => {
                            aoMudar();
                            avisar(ativo.aceita ? 'Aceite desfeito.' : 'Aceita — entra no documento entregue.');
                          })
                          .catch(avisarErro)
                      }
                    >
                      {ativo.aceita ? 'desfazer o aceite' : 'aceitar'}
                    </button>
                  </>
                )}
              </p>
            </div>
          )}

          {ehMaster && (
            <div className="imv-detalhe-acoes">
              <button type="button" className="imv-bt p" onClick={() => aoCorrigir(ativo)}>
                <Traco d={['M4 20h4L19 9l-4-4L4 16z', 'M13.5 6.5l4 4']} />
                Corrigir
              </button>
              <button type="button" className="imv-bt p fantasma perigo" onClick={() => setAApagar(true)}>
                <Traco d={['M4 7h16', 'M9 7V4.5h6V7', 'M6.5 7l1 13h9l1-13']} />
                Apagar
              </button>
            </div>
          )}
        </div>
      )}

      {aApagar && (
        <Confirmar
          titulo="Apagar este lançamento?"
          descricao={
            ativo.origem
              ? `"${ativo.descricao}" veio de consulta. Apagá-lo não devolve o que a consulta custou.`
              : `"${ativo.descricao}" será apagado, com os gravames lançados nele.`
          }
          aoConfirmar={async () => {
            await apiInvestigados.apagarAtivo(investigadoId, ativo.id);
            setAApagar(false);
            aoMudar();
          }}
          aoFechar={() => setAApagar(false)}
        />
      )}
      {gravameAApagar && (
        <Confirmar
          titulo="Apagar este gravame?"
          descricao={`${gravameAApagar.natureza}${gravameAApagar.credor ? ` · ${gravameAApagar.credor}` : ''} sai deste imóvel agora, e não volta.`}
          aoConfirmar={() =>
            void apiInvestigados
              .apagarOnus(investigadoId, ativo.id, gravameAApagar.id)
              .then(aoMudar)
              .catch(avisarErro)
          }
          aoFechar={() => setGravameAApagar(null)}
        />
      )}
    </article>
  );
}

/* ═══ O FORMULÁRIO PASSO A PASSO ═════════════════════════════════════════ */

type GravameLocal = { natureza: string; credor: string; processo: string; valor: string; constituidoEm: string; situacao: string; origem?: OrigemDoCampo };
/** ⭐ 16/10/2026 (PRD §51): uma parte do ato e um coproprietário, como a pessoa digita. */
type ParteLocal = { nome: string; documento: string; papel: PapelDaParteNoAto | '' };
type CoproLocal = { nome: string; documento: string; fracao: string };
const parteVazia = (p: ParteLocal) => !p.nome.trim() && !p.documento.trim() && !p.papel;
const coproVazio = (c: CoproLocal) => !c.nome.trim() && !c.documento.trim() && !c.fracao.trim();

const PASSOS = ['Situação e bem', 'Último ato', 'Quem participou', 'Negócio e avaliação', 'Gravames e certidão', 'Conferir'] as const;

const numero = (t: string) => (t.trim() === '' ? null : Number(t.replace(/\./g, '').replace(',', '.')));
const emCampo = (v: number | null | undefined) =>
  v === null || v === undefined ? '' : v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
/* ⭐ R4 (27/10/2026, PRD §67.5): a área com as quatro casas que a matrícula escreve ("187,4149 m²"); eram duas. */
/**
 * ⭐ §77: os campos que a leitura de uma matrícula JÁ LANÇADA pode completar (só quando estão vazios no lançamento). A
 * situação fica fora de propósito: mudá-la é decisão dele, e a ficha só avisa quando a leitura sugere outra.
 */
const CAMPOS_QUE_A_LEITURA_COMPLETA = [
  'descricao', 'tipoDoImovel', 'numeroDaMatricula', 'cartorio', 'cidade', 'uf', 'areaPrivativa', 'areaConstruida', 'areaDoTerreno',
  'ocorridoEm', 'modalidadeDeAquisicao', 'tabelionato', 'efeitoPatrimonial', 'qualidadeNoAto', 'valorDeclarado', 'percentual',
  'proprietarioAtual', 'certidaoEm', 'matriculaSucessora', 'motivoEncerramento', 'transmitente', 'adquirente', 'vinculoEntrePartes',
] as const;

const emArea4 = (v: number | null | undefined) =>
  v === null || v === undefined ? '' : v.toLocaleString('pt-BR', { maximumFractionDigits: 4 });

/**
 * ⭐⭐ **A FICHA DO IMÓVEL, PASSO A PASSO — a proposta B dele, 16/09/2026.**
 *
 * Seis passos, um assunto por tela, e o último mostra a linha como sai no
 * relatório. **O mesmo formulário lança e corrige** (regra de 10/09): `key` no
 * pai recria os campos quando muda o bem.
 *
 * ⛔ **Nenhum botão apaga o que o caso já tem.** Corrigindo, a ficha manda só os
 * campos que ela edita: `transmitente`, `adquirente` e `papelNoAto` — que ela
 * não mostra — ficam como estão. E a identificação antiga só é trocada se a
 * pessoa preencher número ou cartório.
 */
function FichaDoImovel({
  investigado,
  doCaso,
  corrigindo,
  partindoDe,
  completarCom = null,
  bloqueio = null,
  confirmacao = null,
  aoAlterar,
  aoFechar,
  aoDescartar,
  aoGravar,
}: {
  investigado: Investigado;
  /** ⭐ As partes do caso: a empresa titular que é uma delas soma na ficha dela. */
  doCaso: Investigado[];
  corrigindo: AtivoDoInvestigado | null;
  partindoDe: MatriculaProposta | null;
  /** ⭐ §77: a leitura de uma matrícula já lançada: completa SÓ o que está vazio no lançamento (`corrigindo`). */
  completarCom?: MatriculaProposta | null;
  /** ⭐ §77: "Confirmo que este imóvel é desta parte", ao lado do "Lançar" (papel sem o documento nem o nome). */
  confirmacao?: { texto: string; feita: boolean; aoMudar: (sim: boolean) => void } | null;
  /** ⭐ §77: na ficha que veio de uma leitura, "Cancelar" vira "Descartar" (a leitura sai). */
  aoDescartar?: () => void;
  /** ⭐ §76: por que esta leitura ainda não pode ser lançada (pode ser de outra pessoa; já lançada nesta parte). */
  bloqueio?: string | null;
  /** ⭐ §76: avisa, uma vez, que algo foi digitado (anexar numa ficha em branco a troca pela leitura; digitada, não). */
  aoAlterar?: () => void;
  aoFechar: () => void;
  /** ⭐ PRD §75: no lançamento que partiu da leitura, quantos campos foram mudados à mão (só o número). */
  aoGravar: (corrigidos?: number) => void;
}) {
  const ehMaster = usarEhMaster();
  /*
   * ⭐ §77: "completar o lançamento" — a matrícula já lançada, lida de novo, preenche SÓ o que está vazio nele (⛔ nenhum
   * botão apaga o que o caso já tem). `completados` diz quais campos vieram da leitura, para marcar e mostrar a origem.
   */
  const completando = !!(corrigindo && completarCom);
  const completados = new Set<string>();
  const comLeitura = completando ? ({ ...corrigindo } as Record<string, unknown>) : null;
  if (comLeitura && completarCom) {
    const daLeitura = completarCom as unknown as Record<string, unknown>;
    for (const k of CAMPOS_QUE_A_LEITURA_COMPLETA) {
      const atual = comLeitura[k];
      const novo = daLeitura[k];
      if ((atual === null || atual === undefined || atual === '') && novo !== null && novo !== undefined && novo !== '') {
        comLeitura[k] = novo;
        completados.add(k);
      }
    }
  }
  const inicial = (comLeitura ?? corrigindo ?? partindoDe ?? null) as (Partial<AtivoDoInvestigado> & Partial<MatriculaProposta>) | null;
  const separada = separarIdentificacao(inicial?.identificacao);

  const [passo, setPasso] = useState(0);
  const [visitados, setVisitados] = useState<Set<number>>(new Set([0]));
  const situacaoDada = Boolean(
    inicial?.classificacao && (CLASSES.includes(inicial.classificacao) || EMPRESA_DO_GRUPO.includes(inicial.classificacao)),
  );
  const [classificacao, setClassificacao] = useState<ClassificacaoDoAtivo>(situacaoDada ? inicial!.classificacao! : 'imovel_livre');
  /*
   * ⭐⭐ **NADA VEM MARCADO SEM LEITURA — R4, 27/10/2026 (PRD §67.5).** A matrícula do REL-2026-050 abriu com "Livre"
   * já escolhido, sem nenhum ato lido; e "Livre" aceito por cansaço soma o bem no patrimônio. Sem a situação lida (e no
   * lançamento à mão), nenhuma vem marcada, e o passo pede a escolha.
   */
  const [semSituacao, setSemSituacao] = useState(!situacaoDada);
  /** ⭐ R4: de onde veio o campo, quando ele veio da leitura do PDF. */
  const origemDe = (c: CampoLido): OrigemDoCampo | undefined =>
    completando ? (completados.has(c) ? completarCom?.origens?.[c] : undefined) : partindoDe?.origens?.[c];
  const [descricao, setDescricao] = useState(inicial?.descricao ?? '');
  const [tipoDoImovel, setTipoDoImovel] = useState(inicial?.tipoDoImovel ?? '');
  const [numeroMat, setNumeroMat] = useState(inicial?.numeroDaMatricula ?? separada?.numero ?? '');
  const [cartorio, setCartorio] = useState(inicial?.cartorio ?? separada?.cartorio ?? '');
  const [cidade, setCidade] = useState(inicial?.cidade ?? '');
  const [uf, setUf] = useState(inicial?.uf ?? '');
  const [areaPrivativa, setAreaPrivativa] = useState(emArea4(inicial?.areaPrivativa));
  const [areaConstruida, setAreaConstruida] = useState(emArea4(inicial?.areaConstruida));
  const [areaDoTerreno, setAreaDoTerreno] = useState(emArea4(inicial?.areaDoTerreno));
  const [ocorridoEm, setOcorridoEm] = useState(inicial?.ocorridoEm ?? '');
  const [titulo, setTitulo] = useState(inicial?.modalidadeDeAquisicao ?? '');
  const [tabelionato, setTabelionato] = useState(inicial?.tabelionato ?? '');
  const [efeito, setEfeito] = useState<EfeitoPatrimonialDoAto | ''>(inicial?.efeitoPatrimonial ?? '');
  const [qualidade, setQualidade] = useState(inicial?.qualidadeNoAto ?? '');
  /*
   * ⭐⭐ **AS PARTES DO ATO, EM LISTA — 16/10/2026 (PRD §51).** Cada uma com
   * nome, CPF/CNPJ e o papel no ato. O que vem do servidor manda; sem lista,
   * o nome solto de antes (ou o da leitura colada) vira a primeira parte, com o
   * papel em branco: nada do que estava escrito se perde.
   */
  const [partes, setPartes] = useState<ParteLocal[]>(() => {
    const lista = corrigindo?.partes ?? [];
    if (lista.length > 0) {
      return lista.map((p) => ({ nome: p.nome, documento: p.documento ? documentoNaTela(p.documento) : '', papel: p.papel ?? '' }));
    }
    /* ⭐ R4: as outras partes do último ato, como a leitura do PDF as achou. */
    const daLeitura = partindoDe?.partes ?? [];
    if (daLeitura.length > 0) {
      return daLeitura.map((p) => ({
        nome: p.nome,
        documento: p.documento ? documentoNaTela(p.documento) : '',
        papel: PAPEIS_DA_PARTE.find((x) => x.chave === p.papel)?.chave ?? '',
      }));
    }
    const solto = inicial?.outraParte?.trim();
    return solto ? [{ nome: solto, documento: '', papel: '' }] : [];
  });
  /* ⭐ Os coproprietários do mesmo bem. ⛔ A fração deles não muda a do investigado. */
  const [copros, setCopros] = useState<CoproLocal[]>(() =>
    (corrigindo?.coproprietarios ?? []).map((c) => ({
      nome: c.nome,
      documento: c.documento ? documentoNaTela(c.documento) : '',
      fracao: c.fracao === null ? '' : c.fracao.toLocaleString('pt-BR', { maximumFractionDigits: 2 }),
    })),
  );
  const [relacao, setRelacao] = useState(inicial?.vinculoEntrePartes ?? '');
  const [proprietarioAtual, setProprietarioAtual] = useState(inicial?.proprietarioAtual ?? '');
  /*
   * ⭐⭐ **"LIVRE, EMPRESA DO GRUPO": QUEM É A TITULAR — 16/10/2026.** Na ficha
   * de uma empresa do caso, a titular pode ser ela mesma (soma) ou outra empresa
   * do grupo; na ficha de pessoa física, é sempre outra. ⚠️ A tela só PERGUNTA:
   * quem decide se soma é o servidor, pela titular (`decidirEmpresaDoGrupo`).
   */
  const parteEhEmpresa = investigado.tipo === 'pj';
  const [docTitular, setDocTitular] = useState(
    corrigindo?.documentoDoTitular ? documentoNaTela(corrigindo.documentoDoTitular) : '',
  );
  const [titularPropria, setTitularPropria] = useState(
    parteEhEmpresa &&
      inicial?.classificacao !== 'imovel_de_empresa_do_grupo' &&
      !(corrigindo?.documentoDoTitular && soDigitos(corrigindo.documentoDoTitular) !== soDigitos(investigado.documento ?? '')),
  );
  const [conexao, setConexao] = useState(inicial?.elementoDeConexao ?? '');
  const [sucessora, setSucessora] = useState(inicial?.matriculaSucessora ?? '');
  const [motivo, setMotivo] = useState(inicial?.motivoEncerramento ?? '');
  const [valorDeclarado, setValorDeclarado] = useState(emCampo(inicial?.valorDeclarado));
  const [valorGlobal, setValorGlobal] = useState(inicial?.valorGlobal ?? false);
  const [percentual, setPercentual] = useState(emCampo(inicial?.percentual));
  const [avaliacoes, setAvaliacoes] = useState<LinhaDeAvaliacao[]>(() => {
    const lista = inicial?.avaliacoes ?? [];
    if (lista.length > 0) {
      return lista.map((a) => ({
        id: a.id,
        modalidade: a.modalidade ?? '',
        valor: a.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        referencia: a.referencia ?? '',
        avaliadaEm: a.avaliadaEm ?? '',
        conta: a.entraNaConta,
      }));
    }
    if (inicial?.avaliacao !== null && inicial?.avaliacao !== undefined) {
      return [{ modalidade: inicial.modalidadeDaAvaliacao ?? '', valor: emCampo(inicial.avaliacao), referencia: '', avaliadaEm: '', conta: true }];
    }
    return [];
  });
  const [certidaoEm, setCertidaoEm] = useState(inicial?.certidaoEm ?? '');
  const [gravames, setGravames] = useState<GravameLocal[]>(
    (partindoDe?.onus ?? []).map((o) => ({
      natureza: o.natureza,
      credor: o.credor ?? '',
      processo: '',
      valor: emCampo(o.valor),
      constituidoEm: o.constituidoEm ?? '',
      situacao: o.situacao ?? '',
      origem: o.origem,
    })),
  );
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  /* Os gravames de um bem que já existe vêm do servidor e mudam na hora. */
  const [onusExistentes, setOnusExistentes] = useState(corrigindo?.onus ?? []);
  /* ⛔ Apagar um gravame pergunta antes — a regra dele, 18/09/2026. */
  const [gravameAApagar, setGravameAApagar] = useState<AtivoDoInvestigado['onus'][number] | null>(null);
  /* ⭐ Corrigir um gravame já lançado — 27/10/2026, pedido dele (PRD §65.7): a linha vira campos, e grava na hora. */
  const [gravameEmEdicao, setGravameEmEdicao] = useState<(GravameLocal & { id: string }) | null>(null);
  const [gravandoGravame, setGravandoGravame] = useState(false);

  async function gravarGravame() {
    if (!corrigindo || !gravameEmEdicao) return;
    if (gravameEmEdicao.natureza.trim().length < 2) {
      avisar('Escreva a natureza do gravame.');
      return;
    }
    setGravandoGravame(true);
    try {
      const { ativo } = await apiInvestigados.corrigirOnus(investigado.id, corrigindo.id, gravameEmEdicao.id, {
        natureza: gravameEmEdicao.natureza.trim(),
        credor: gravameEmEdicao.credor.trim() || null,
        processo: gravameEmEdicao.processo.trim() || null,
        valor: numero(gravameEmEdicao.valor),
        constituidoEm: gravameEmEdicao.constituidoEm || null,
        situacao: gravameEmEdicao.situacao || null,
      });
      setOnusExistentes(ativo.onus);
      setGravameEmEdicao(null);
      avisar('Gravame corrigido.');
    } catch (err) {
      avisarErro(err);
    } finally {
      setGravandoGravame(false);
    }
  }

  const ehEmpresaDoGrupo = EMPRESA_DO_GRUPO.includes(classificacao);
  const titularEhAParte = ehEmpresaDoGrupo && parteEhEmpresa && titularPropria;
  /* ⭐ A face que a tela supõe, para mostrar os campos certos. O servidor confere
     e decide de novo, pela titular. */
  const classeEfetiva: ClassificacaoDoAtivo = ehEmpresaDoGrupo
    ? titularEhAParte
      ? 'imovel_livre_empresa_do_grupo'
      : 'imovel_de_empresa_do_grupo'
    : classificacao;
  const pedeValor = PEDE_VALOR.includes(classeEfetiva);
  /* ⚠️ Aviso, nunca trava: a soma das frações com a participação do investigado. */
  const somaDasFracoes = (() => {
    const fracoes = copros.map((c) => numero(c.fracao)).filter((f): f is number => f !== null && Number.isFinite(f));
    if (fracoes.length === 0) return null;
    const total = fracoes.reduce((a, b) => a + b, 0) + (numero(percentual) ?? 0);
    return total > 100 + 1e-9 ? total : null;
  })();
  /* O aviso some assim que o passo deixa de ter o problema. */
  useEffect(() => {
    if (erro && !problemaDoPasso(passo)) setErro(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descricao, conexao, avaliacoes, classificacao, semSituacao, partes, copros, proprietarioAtual, docTitular, titularPropria]);
  const lido = (v: unknown, chave?: string) =>
    completando ? (chave && completados.has(chave) ? ' lido' : '') : partindoDe && v !== null && v !== undefined && v !== '' ? ' lido' : '';

  /** O que impede de avançar deste passo — ou nulo. */
  function problemaDoPasso(p: number): string | null {
    if (p === 0 && semSituacao) return 'Escolha a situação do bem: nenhuma vem marcada sem leitura.';
    if (p === 0 && descricao.trim().length < 2) return 'Escreva o endereço do bem: é o que sai impresso.';
    if (p === 2 && classificacao === 'imovel_de_terceiro' && conexao.trim().length === 0) {
      return 'Em nome de terceiro, o elemento de conexão é obrigatório.';
    }
    if (p === 2) {
      /* ⭐ 16/10/2026: as listas e a empresa titular. A causa, e onde ela está. */
      const i = partes.findIndex((x) => !parteVazia(x) && x.nome.trim().length < 2);
      if (i >= 0) return `Escreva o nome da ${i + 1}ª parte do ato, ou tire a linha.`;
      const j = partes.findIndex((x) => !documentoConfere(x.documento));
      if (j >= 0) return `O CPF ou CNPJ da ${j + 1}ª parte do ato não confere pelo dígito verificador.`;
      const k = copros.findIndex((x) => !coproVazio(x) && x.nome.trim().length < 2);
      if (k >= 0) return `Escreva o nome do ${k + 1}º coproprietário, ou tire a linha.`;
      const m = copros.findIndex((x) => !documentoConfere(x.documento));
      if (m >= 0) return `O CPF ou CNPJ do ${m + 1}º coproprietário não confere pelo dígito verificador.`;
      const n = copros.findIndex((x) => x.fracao.trim() !== '' && !(numero(x.fracao)! >= 0 && numero(x.fracao)! <= 100));
      if (n >= 0) return `A fração do ${n + 1}º coproprietário vai de 0 a 100%.`;
      if (ehEmpresaDoGrupo && !titularEhAParte) {
        if (proprietarioAtual.trim().length < 2) {
          return parteEhEmpresa
            ? 'Diga o nome da empresa titular, como consta na matrícula.'
            : 'Na ficha de pessoa física, "Livre, empresa do grupo" pede a empresa titular: o nome e o CNPJ dela.';
        }
        if (!cnpjValido(docTitular)) {
          return soDigitos(docTitular)
            ? 'O CNPJ da empresa titular não confere pelo dígito verificador.'
            : 'Informe o CNPJ da empresa titular: é por ele que o Hub sabe se ela é parte do caso.';
        }
        if (doCaso.some((x) => x.id !== investigado.id && soDigitos(x.documento ?? '') === soDigitos(docTitular))) {
          return 'Este CNPJ é de uma parte do caso: lance o imóvel na ficha dela, onde ele soma como livre.';
        }
        if (conexao.trim().length < 10) {
          return 'Em nome de empresa do grupo que não é parte do caso, o elemento de conexão é obrigatório.';
        }
      }
    }
    if (p === 3 && pedeValor) {
      const linhas = avaliacoes.filter((a) => a.valor.trim() !== '' || a.modalidade !== '' || a.referencia.trim() !== '');
      if (linhas.some((a) => numero(a.valor) === null || !(numero(a.valor)! > 0))) return 'Informe o valor de cada avaliação, ou tire a linha.';
      if (linhas.some((a) => !a.id && a.modalidade === '')) return 'Diga de onde veio cada avaliação nova.';
    }
    return null;
  }

  function irPara(p: number) {
    /* Avançar confere o passo em que se está; voltar, nunca. */
    if (p > passo) {
      for (let k = passo; k < p; k++) {
        const x = problemaDoPasso(k);
        if (x) {
          setPasso(k);
          setErro(x);
          return;
        }
      }
    }
    setErro(null);
    setPasso(p);
    setVisitados((v) => new Set([...v, p]));
  }

  const pendencias = [
    !numeroMat.trim() && 'número da matrícula',
    !cartorio.trim() && 'cartório',
    !ocorridoEm && 'data do ato',
    !titulo.trim() && 'escritura/título',
    !tabelionato.trim() && 'tabelionato/origem',
    !qualidade.trim() && 'qualidade do investigado',
    partes.every(parteVazia) && 'partes do ato',
    !relacao.trim() && 'relação',
    !valorDeclarado.trim() && !valorGlobal && 'valor do negócio',
    !efeito && 'situação patrimonial',
    !certidaoEm && 'data da certidão',
  ].filter((x): x is string => Boolean(x));

  /** O que vai ao servidor, montado do que está na ficha agora. */
  function montarDados() {
    const linhas = avaliacoes.filter((a) => a.valor.trim() !== '' || a.modalidade !== '' || a.referencia.trim() !== '');
    const composta =
      numeroMat.trim() || cartorio.trim()
        ? `${numeroMat.trim() ? `MATRÍCULA ${numeroMat.trim()}` : ''}${numeroMat.trim() && cartorio.trim() ? ' · ' : ''}${cartorio.trim()}`
        : null;
    const dados = {
      /* ⭐ O servidor confere a face pela titular e pode trocá-la; nunca soma pela tela. */
      classificacao: classeEfetiva,
      especie: null,
      descricao,
      /* ⛔ A identificação antiga só se troca quando a pessoa preencheu as partes. */
      ...(composta !== null || !corrigindo ? { identificacao: composta } : {}),
      numeroDaMatricula: numeroMat.trim() || null,
      cartorio: cartorio.trim() || null,
      ...(pedeValor
        ? {
            avaliacoes: linhas.map((a) => ({
              ...(a.id ? { id: a.id } : {}),
              modalidade: a.modalidade || null,
              valor: numero(a.valor)!,
              referencia: a.referencia.trim() || null,
              avaliadaEm: a.avaliadaEm || null,
              entraNaConta: a.conta,
            })),
          }
        : {}),
      percentual: numero(percentual),
      valorDeclarado: numero(valorDeclarado),
      valorGlobal,
      ocorridoEm: ocorridoEm || null,
      modalidadeDeAquisicao: titulo.trim() || null,
      tabelionato: tabelionato.trim() || null,
      efeitoPatrimonial: efeito || null,
      qualidadeNoAto: qualidade.trim() || null,
      /* ⭐ 16/10/2026: as listas mandam, e o servidor espelha os nomes em `outraParte`. */
      partes: partes
        .filter((x) => !parteVazia(x))
        .map((x) => ({ nome: x.nome.trim(), documento: soDigitos(x.documento) || null, papel: x.papel || null })),
      coproprietarios: copros
        .filter((x) => !coproVazio(x))
        .map((x) => ({ nome: x.nome.trim(), documento: soDigitos(x.documento) || null, fracao: numero(x.fracao) })),
      documentoDoTitular: ehEmpresaDoGrupo && !titularEhAParte ? soDigitos(docTitular) || null : null,
      vinculoEntrePartes: relacao.trim() || null,
      /* ⚠️ Na "Livre, empresa do grupo" cuja titular é a própria parte, não há outra titular a gravar. */
      proprietarioAtual: titularEhAParte ? null : proprietarioAtual.trim() || null,
      elementoDeConexao: conexao.trim() || null,
      matriculaSucessora: sucessora.trim() || null,
      motivoEncerramento: motivo.trim() || null,
      tipoDoImovel: tipoDoImovel.trim() || null,
      cidade: cidade.trim() || null,
      uf: uf.trim() || null,
      areaPrivativa: numero(areaPrivativa),
      areaDoTerreno: numero(areaDoTerreno),
      areaConstruida: numero(areaConstruida),
      certidaoEm: certidaoEm || null,
    };
    return dados;
  }

  /*
   * ⭐⭐ PRD §75: a foto de como a proposta da leitura chegou à ficha (no primeiro desenho, antes de qualquer mudança),
   * para contar quantos campos foram mudados à mão no "Lançar imóvel". Só o número sai daqui; é com ele que a volta
   * automática do comitê compara o modelo novo com o anterior.
   */
  const comoChegou = useRef<{ campos: Record<string, string>; gravames: string } | null>(null);
  const fotoDosGravames = () => JSON.stringify(gravames.map((g) => [g.natureza.trim(), g.credor.trim(), g.processo.trim(), g.valor.trim(), g.constituidoEm]));
  const paraComparar = (d: Record<string, unknown>) => Object.fromEntries(Object.entries(d).map(([k, v]) => [k, JSON.stringify(v ?? null)]));
  function camposMudados(d: Record<string, unknown>): number | undefined {
    const foto = comoChegou.current;
    if (!foto) return undefined;
    const agora = paraComparar(d);
    const chaves = new Set([...Object.keys(foto.campos), ...Object.keys(agora)]);
    return [...chaves].filter((k) => foto.campos[k] !== agora[k]).length + (fotoDosGravames() !== foto.gravames ? 1 : 0);
  }

  async function gravar() {
    for (let k = 0; k < PASSOS.length - 1; k++) {
      const x = problemaDoPasso(k);
      if (x) {
        setPasso(k);
        setErro(x);
        return;
      }
    }
    setGravando(true);
    setErro(null);
    const dados = montarDados();
    const corrigidos = camposMudados(dados);
    try {
      if (corrigindo) {
        const r = (await apiInvestigados.editarAtivo(investigado.id, corrigindo.id, dados)) as unknown as { ativo?: AtivoDoInvestigado };
        avisar(r.ativo?.avisoDasFracoes ? `Lançamento corrigido. ${r.ativo.avisoDasFracoes}` : 'Lançamento corrigido.');
        aoGravar();
        return;
      }
      const { ativo: criado } = await apiInvestigados.acrescentarAtivo(investigado.id, dados);
      if (criado.avisoDasFracoes) avisar(criado.avisoDasFracoes);
      /* ⭐ Os gravames seguem junto — a falha de um não derruba o lançamento. */
      const aLancar = gravames.filter((g) => g.natureza.trim().length >= 2);
      let falharam = 0;
      for (const g of aLancar) {
        try {
          await apiInvestigados.acrescentarOnus(investigado.id, criado.id, {
            natureza: g.natureza.trim(),
            credor: g.credor.trim() || null,
            processo: g.processo.trim() || null,
            valor: numero(g.valor),
            constituidoEm: g.constituidoEm || null,
            situacao: g.situacao || null,
          });
        } catch {
          falharam += 1;
        }
      }
      avisar(
        aLancar.length === 0
          ? 'Imóvel lançado.'
          : falharam === 0
            ? `Imóvel lançado, com ${aLancar.length} gravame(s).`
            : `Imóvel lançado; ${falharam} gravame(s) falharam e precisam ser lançados à mão.`,
      );
      aoGravar(corrigidos);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gravar.');
    } finally {
      setGravando(false);
    }
  }

  const previa = useMemo(
    () => ({
      investigado: investigado.nome,
      qualidade,
      data: emData(ocorridoEm),
      titulo,
      origem: tabelionato,
      outraParte: partes
        .filter((x) => !parteVazia(x))
        .map((x) => x.nome.trim().toLocaleUpperCase('pt-BR'))
        .join('; '),
      /* ⭐ 16/10/2026: a frase do ato e os coproprietários, como o servidor os escreve. */
      frase: fraseDoAto(
        investigado.nome,
        partes
          .filter((x) => !parteVazia(x))
          .map((x) => ({ nome: x.nome.trim().toLocaleUpperCase('pt-BR'), documento: soDigitos(x.documento) || null, papel: x.papel || null })),
      ),
      coproprietarios: copros
        .filter((x) => !coproVazio(x))
        .map((x) => {
          const doc = documentoComRotulo(x.documento);
          const fr = numero(x.fracao);
          return `${x.nome.trim().toLocaleUpperCase('pt-BR')}${doc ? ` (${doc})` : ''}, ${fr !== null ? `${fr.toLocaleString('pt-BR')}%` : 'fração não informada'}`;
        })
        .join('; '),
      empresaTitular:
        ehEmpresaDoGrupo && !titularEhAParte && proprietarioAtual.trim()
          ? `${proprietarioAtual.trim().toLocaleUpperCase('pt-BR')}${documentoComRotulo(docTitular) ? ` (${documentoComRotulo(docTitular)})` : ''}`
          : '',
      relacao,
      objeto: descricao,
      matricula: numeroMat,
      cartorio,
      cidadeUf: [cidade, uf].filter(Boolean).join('/'),
      valor: valorDeclarado.trim() ? `R$ ${valorDeclarado.trim()}` : '',
      valorGlobal,
      situacao: efeito ? ROTULO_DO_EFEITO[efeito] : '',
      classificacao: classeEfetiva,
    }),
    [investigado.nome, qualidade, ocorridoEm, titulo, tabelionato, partes, copros, ehEmpresaDoGrupo, titularEhAParte, proprietarioAtual, docTitular, relacao, descricao, numeroMat, cartorio, cidade, uf, valorDeclarado, valorGlobal, efeito, classeEfetiva],
  );

  if (comoChegou.current === null && partindoDe && !corrigindo) comoChegou.current = { campos: paraComparar(montarDados()), gravames: fotoDosGravames() };
  /* ⭐ §76: a foto do primeiro desenho, para saber se algo foi digitado depois. */
  const retrato = JSON.stringify(paraComparar(montarDados())) + fotoDosGravames();
  const primeiroRetrato = useRef<string | null>(null);
  if (primeiroRetrato.current === null) primeiroRetrato.current = retrato;
  const avisouAlteracao = useRef(false);
  useEffect(() => {
    if (!avisouAlteracao.current && retrato !== primeiroRetrato.current) {
      avisouAlteracao.current = true;
      aoAlterar?.();
    }
  }, [retrato, aoAlterar]);

  return (
    <section className="imv-ficha" aria-label={corrigindo ? 'Corrigir imóvel' : 'Lançar imóvel'}>
      <header className="imv-ficha-topo">
        <div>
          <span className="s-sobrelinha">
            {completando ? 'Completando com a leitura' : corrigindo ? 'Corrigindo' : partindoDe ? 'Novo imóvel · lido da certidão' : 'Novo imóvel'} · passo {passo + 1} de{' '}
            {PASSOS.length}
          </span>
          <h3>
            {corrigindo ? 'Corrigir imóvel de ' : 'Lançar imóvel de '}
            {investigado.nome}
          </h3>
          <p>
            Um assunto por vez. Volte a qualquer passo sem perder o que digitou.
            {partindoDe && (
              <>
                {' '}
                <span className="imv-lido-legenda">
                  <i /> veio da leitura do PDF — confira
                </span>
                {Object.values(partindoDe.origens ?? {}).some((o) => o?.conferir) && (
                  <>
                    {' '}
                    <span className="imv-lido-legenda conferir">
                      <i /> o Hub tem dúvida: veja o motivo
                    </span>
                  </>
                )}
              </>
            )}
          </p>
        </div>
        <button type="button" className="imv-fechar" onClick={aoFechar} aria-label="Fechar sem gravar">
          <Traco d={['M6 6l12 12', 'M18 6 6 18']} />
        </button>
      </header>

      <ol className="imv-passos">
        {PASSOS.map((t, i) => (
          <li key={t} className={i === passo ? 'at' : visitados.has(i) && i < passo ? 'feito' : visitados.has(i) ? 'visto' : undefined}>
            <button type="button" onClick={() => irPara(i)} aria-current={i === passo ? 'step' : undefined}>
              <span className="imv-n">{i < passo && visitados.has(i) ? <Traco d={['M5 12.5 10 17 19 7.5']} /> : i + 1}</span>
              {t}
            </button>
          </li>
        ))}
      </ol>

      {completando && passo === 0 && (
        <div className="imv-ressalvas">
          <strong>
            A leitura completou {completados.size} {completados.size === 1 ? 'campo que estava vazio' : 'campos que estavam vazios'}
          </strong>{' '}
          (marcados como lidos); o resto do lançamento ficou como estava.
          {completarCom?.classificacao && completarCom.classificacao !== corrigindo?.classificacao && (
            <ul>
              <li>
                A leitura sugere "{CURTO[completarCom.classificacao as keyof typeof CURTO] ?? completarCom.classificacao}"; o lançamento diz "
                {CURTO[corrigindo!.classificacao as keyof typeof CURTO] ?? corrigindo!.classificacao}". A situação não muda sozinha: confira.
              </li>
            </ul>
          )}
        </div>
      )}
      {(() => {
        const fonte = partindoDe ?? completarCom;
        return fonte && fonte.ressalvas.length > 0 && passo === 0 ? (
          <div className="imv-ressalvas">
            <strong>A leitura deixou {fonte.ressalvas.length} ponto(s) a conferir:</strong>
            <ul>
              {fonte.ressalvas.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        ) : null;
      })()}

      <div className="imv-corpo">
        <div className="imv-conteudo">
          {/* ⭐ 16/10/2026: "Quem participou" ganhou duas tabelas e passou à ajuda em faixa,
              como os outros passos de tabela; ao lado, o CNPJ não cabia. */}
          {(passo === 2 || passo === 3 || passo === 4) && <Ajuda passo={passo} faixa />}
          {passo === 0 && (
            <>
              <h4 className="imv-passo-titulo">A situação e o bem</h4>
              <p className="imv-passo-sub">
                A situação decide a soma do patrimônio; o bem é a coluna <b>Bem</b> da tabela do relatório.
              </p>
              <div className="imv-seg" role="radiogroup" aria-label="Situação do bem">
                {CLASSES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={!semSituacao && (classificacao === c || (c === 'imovel_livre_empresa_do_grupo' && ehEmpresaDoGrupo))}
                    className={`imv-op ${CLASSE_CSS[c]}${!semSituacao && (classificacao === c || (c === 'imovel_livre_empresa_do_grupo' && ehEmpresaDoGrupo)) ? ' on' : ''}`}
                    onClick={() => {
                      setClassificacao(c);
                      setSemSituacao(false);
                    }}
                  >
                    <i />
                    {CURTO[c]}
                  </button>
                ))}
              </div>
              {semSituacao ? (
                <p className="imv-def neutro">
                  {partindoDe ? 'A leitura não decidiu a situação: escolha uma, olhando os atos.' : 'Escolha a situação do bem.'}
                </p>
              ) : (
                <p className={`imv-def ${CLASSE_CSS[classificacao]}`}>{DEFINICAO[classificacao]}</p>
              )}
              {origemDe('classificacao') && (
                <OrigemDoLido
                  origem={origemDe('classificacao')!}
                  porque
                  aoTrocar={(v) => {
                    setClassificacao(v as ClassificacaoDoAtivo);
                    setSemSituacao(false);
                  }}
                />
              )}
              <div className="imv-grade-campos">
                {/* ⭐ 16/10/2026 (PRD §51): "Objeto" passou a se chamar "Endereço" (só o nome,
                    decisão dele). O campo é o mesmo, e continua sendo a coluna Bem. */}
                <Campo rotulo="Endereço" obrigatorio col={6} lido={lido(inicial?.descricao, 'descricao')} origem={origemDe('descricao')} aoTrocar={(v) => setDescricao(String(v))}>
                  <textarea
                    rows={2}
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder="Rua Exemplo, 100, apto 142, Cidade Fictícia/SP, com duas vagas"
                  />
                </Campo>
                {/*
                 * ⭐ R4: o parágrafo do imóvel como o papel o escreve, para consulta ao lado do endereço. ⛔ Não é
                 * gravado: o endereço é o que sai impresso; o parágrafo inteiro é a matrícula, que o caso não guarda.
                 */}
                {partindoDe?.descricaoDoPapel && partindoDe.descricaoDoPapel !== descricao && (
                  <details className="imv-campo c6 imv-papel-descricao">
                    <summary>Como o papel descreve o imóvel</summary>
                    <p>{partindoDe.descricaoDoPapel}</p>
                  </details>
                )}
                <Campo rotulo="Tipo" col={2} lido={lido(inicial?.tipoDoImovel, 'tipoDoImovel')} origem={origemDe('tipoDoImovel')} aoTrocar={(v) => setTipoDoImovel(String(v))}>
                  <input list="imv-tipos" value={tipoDoImovel} onChange={(e) => setTipoDoImovel(e.target.value)} placeholder="Apartamento" />
                </Campo>
                <Campo rotulo="Cidade" col={3} lido={lido(inicial?.cidade, 'cidade')} origem={origemDe('cidade')} aoTrocar={(v) => setCidade(String(v))}>
                  <input value={cidade} onChange={(e) => setCidade(e.target.value)} />
                </Campo>
                <Campo rotulo="UF" col={1} lido={lido(inicial?.uf, 'uf')} origem={origemDe('uf')} aoTrocar={(v) => setUf(String(v).toUpperCase())}>
                  <input value={uf} maxLength={2} onChange={(e) => setUf(e.target.value.toUpperCase())} />
                </Campo>
                <Campo rotulo="Matrícula nº" col={2} lido={lido(inicial?.numeroDaMatricula, 'numeroDaMatricula')} mono origem={origemDe('numeroDaMatricula')} aoTrocar={(v) => setNumeroMat(String(v))}>
                  <input value={numeroMat} onChange={(e) => setNumeroMat(e.target.value)} placeholder="49.901" />
                </Campo>
                <Campo rotulo="Cartório" col={4} lido={lido(inicial?.cartorio, 'cartorio')} origem={origemDe('cartorio')} aoTrocar={(v) => setCartorio(String(v))}>
                  <input value={cartorio} onChange={(e) => setCartorio(e.target.value)} placeholder="2º Registro de Imóveis de São Caetano do Sul" />
                </Campo>
                <Campo rotulo="Área privativa" col={2} sufixo="m²" mono lido={lido(inicial?.areaPrivativa, 'areaPrivativa')} origem={origemDe('areaPrivativa')} aoTrocar={(v) => setAreaPrivativa(emArea4(Number(v)))}>
                  <input inputMode="decimal" value={areaPrivativa} onChange={(e) => setAreaPrivativa(e.target.value)} placeholder="—" />
                </Campo>
                <Campo rotulo="Área construída" col={2} sufixo="m²" mono lido={lido(inicial?.areaConstruida, 'areaConstruida')} origem={origemDe('areaConstruida')} aoTrocar={(v) => setAreaConstruida(emArea4(Number(v)))}>
                  <input inputMode="decimal" value={areaConstruida} onChange={(e) => setAreaConstruida(e.target.value)} placeholder="—" />
                </Campo>
                <Campo rotulo="Área do terreno" col={2} sufixo="m²" mono lido={lido(inicial?.areaDoTerreno, 'areaDoTerreno')} origem={origemDe('areaDoTerreno')} aoTrocar={(v) => setAreaDoTerreno(emArea4(Number(v)))}>
                  <input inputMode="decimal" value={areaDoTerreno} onChange={(e) => setAreaDoTerreno(e.target.value)} placeholder="—" />
                </Campo>
              </div>
              <datalist id="imv-tipos">
                {TIPOS_DE_IMOVEL.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              <p className="imv-nota">
                Área em branco fica em branco — nunca vira zero. Nomeie o registro que detém o fato, nunca a plataforma
                consultada.
              </p>
            </>
          )}

          {passo === 1 && (
            <>
              <h4 className="imv-passo-titulo">O último ato do investigado</h4>
              <p className="imv-passo-sub">
                É o que sai nas colunas <b>Ato</b> e <b>Negócio</b> da tabela do relatório.
              </p>
              <div className="imv-grade-campos">
                <Campo rotulo="Data do ato" col={2} mono lido={lido(inicial?.ocorridoEm, 'ocorridoEm')} origem={origemDe('ocorridoEm')} aoTrocar={(v) => setOcorridoEm(String(v))}>
                  <input type="date" value={ocorridoEm} onChange={(e) => setOcorridoEm(e.target.value)} />
                </Campo>
                <Campo rotulo="Situação patrimonial" col={4} lido={lido(inicial?.efeitoPatrimonial, 'efeitoPatrimonial')} origem={origemDe('efeitoPatrimonial')}>
                  <select value={efeito} onChange={(e) => setEfeito(e.target.value as EfeitoPatrimonialDoAto | '')}>
                    <option value="">—</option>
                    <OpcoesAgrupadas itens={EFEITOS_DO_ATO} atual={efeito} />
                  </select>
                </Campo>
                <Campo rotulo="Escritura / título" col={6} lido={lido(inicial?.modalidadeDeAquisicao, 'modalidadeDeAquisicao')} origem={origemDe('modalidadeDeAquisicao')} aoTrocar={(v) => setTitulo(String(v))} nota="Lista dos tipos de ato — digite para filtrar; o que faltar, escreva livre.">
                  <input list="imv-titulos" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Escritura pública de venda e compra" />
                </Campo>
                <Campo rotulo="Tabelionato / origem" col={6} lido={lido(inicial?.tabelionato, 'tabelionato')} origem={origemDe('tabelionato')} aoTrocar={(v) => setTabelionato(String(v))} nota="Ex.: 14º Tabelionato de Notas de São Paulo, livro 3.456, fls. 123 — ou instrumento particular no âmbito do SFH.">
                  <input value={tabelionato} onChange={(e) => setTabelionato(e.target.value)} />
                </Campo>
              </div>
              <datalist id="imv-titulos">
                {TIPOS_DE_ATO.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </>
          )}

          {passo === 2 && (
            <>
              <h4 className="imv-passo-titulo">Quem participou</h4>
              <p className="imv-passo-sub">
                O investigado, as outras partes do ato e quem mais é dono do bem. Ninguém daqui vira investigado nem
                conta no limite do contrato.
              </p>
              <div className="imv-grade-campos">
                <Campo rotulo="Qualidade do investigado" col={3} lido={lido(inicial?.qualidadeNoAto, 'qualidadeNoAto')} origem={origemDe('qualidadeNoAto')} aoTrocar={(v) => setQualidade(String(v))}>
                  <input list="imv-qualidades" value={qualidade} onChange={(e) => setQualidade(e.target.value)} placeholder="Comprador" />
                </Campo>
                <Campo
                  rotulo="Relação"
                  col={3}
                  nota="Só quando o documento demonstra. Sobrenome igual não é parentesco."
                  lido={lido(inicial?.vinculoEntrePartes, 'vinculoEntrePartes')}
                  origem={origemDe('vinculoEntrePartes')}
                  aoTrocar={(v) => setRelacao(String(v))}
                  porque
                >
                  <input list="imv-relacoes" value={relacao} onChange={(e) => setRelacao(e.target.value)} placeholder="Negocial" />
                </Campo>
                {/*
                 * ⭐⭐ "LIVRE, EMPRESA DO GRUPO": QUEM É A TITULAR — 16/10/2026. A tela pergunta;
                 * o servidor decide se soma, pela titular.
                 */}
                {ehEmpresaDoGrupo && (
                  <div className="imv-campo c6">
                    <span className="imv-rotulo">A empresa titular</span>
                    {parteEhEmpresa ? (
                      <div className="imv-seg" role="radiogroup" aria-label="A empresa titular">
                        <button type="button" role="radio" aria-checked={titularPropria} className={`imv-op neutro${titularPropria ? ' on' : ''}`} onClick={() => setTitularPropria(true)}>
                          <i />
                          A própria {investigado.nome}
                        </button>
                        <button type="button" role="radio" aria-checked={!titularPropria} className={`imv-op neutro${!titularPropria ? ' on' : ''}`} onClick={() => setTitularPropria(false)}>
                          <i />
                          Outra empresa do grupo
                        </button>
                      </div>
                    ) : null}
                    <small>
                      {titularEhAParte
                        ? 'Parte do caso: o imóvel soma como livre, e o relatório diz que é da empresa do grupo.'
                        : 'Se a empresa é parte do caso, lance o imóvel na ficha dela, onde soma como livre. Se não é, ele só demonstra o vínculo e não soma.'}
                    </small>
                  </div>
                )}
                {ehEmpresaDoGrupo && !titularEhAParte && (
                  <>
                    <Campo rotulo="Empresa titular" obrigatorio col={3}>
                      <input value={proprietarioAtual} onChange={(e) => setProprietarioAtual(e.target.value.toLocaleUpperCase('pt-BR'))} placeholder="GAMA EXEMPLO COMÉRCIO LTDA" />
                    </Campo>
                    <Campo rotulo="CNPJ da empresa titular" obrigatorio col={3} mono>
                      <input
                        inputMode="numeric"
                        value={docTitular}
                        onChange={(e) => setDocTitular(e.target.value)}
                        onBlur={(e) => setDocTitular(documentoNaTela(e.target.value))}
                        placeholder="00.000.000/0000-00"
                      />
                    </Campo>
                  </>
                )}
                {(classificacao === 'imovel_transmitido' || classificacao === 'imovel_de_terceiro') && (
                  <Campo rotulo={classificacao === 'imovel_de_terceiro' ? 'Proprietário registral' : 'Proprietário atual'} col={3} lido={lido(inicial?.proprietarioAtual, 'proprietarioAtual')} origem={origemDe('proprietarioAtual')}>
                    <input value={proprietarioAtual} onChange={(e) => setProprietarioAtual(e.target.value)} placeholder="Quem consta na matrícula hoje" />
                  </Campo>
                )}
                {(classificacao === 'imovel_de_terceiro' || (ehEmpresaDoGrupo && !titularEhAParte)) && (
                  <Campo
                    rotulo="Elemento de conexão"
                    obrigatorio
                    col={6}
                    nota="O fato objetivo que justifica olhar este imóvel — não a conclusão. “Reside no endereço que o investigado declarou” entra; “é laranja do devedor” não."
                  >
                    <input
                      value={conexao}
                      onChange={(e) => setConexao(e.target.value)}
                      placeholder={ehEmpresaDoGrupo ? 'A empresa titular tem o investigado como sócio administrador' : 'O endereço declarado pelo investigado é o deste imóvel'}
                    />
                  </Campo>
                )}
                {classificacao === 'imovel_matricula_encerrada' && (
                  <>
                    <Campo rotulo="Matrícula sucessora" col={2} mono lido={lido(inicial?.matriculaSucessora, 'matriculaSucessora')} origem={origemDe('matriculaSucessora')}>
                      <input value={sucessora} onChange={(e) => setSucessora(e.target.value)} />
                    </Campo>
                    <Campo rotulo="Motivo do encerramento" col={4} lido={lido(inicial?.motivoEncerramento, 'motivoEncerramento')} origem={origemDe('motivoEncerramento')}>
                      <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Unificação, desmembramento, loteamento" />
                    </Campo>
                  </>
                )}
              </div>

              {/*
               * ⭐⭐ AS PARTES DO ATO — 16/10/2026 (PRD §51). Cada uma com nome, CPF/CNPJ e o
               * papel dela no ato. É do papel que sai a frase do relatório: "adquiriu de",
               * "deu o bem em alienação fiduciária a".
               */}
              <div className="imv-bloco">
                <h5 className="imv-rot">Partes do ato</h5>
                {origemDe('partes') && <OrigemDoLido origem={origemDe('partes')!} />}
                <div className="imv-tabela">
                  <div className="imv-tl imv-tl-cab imv-parte-grade">
                    <span>Nome</span>
                    <span>CPF ou CNPJ</span>
                    <span>Papel no ato</span>
                    <span />
                  </div>
                  {partes.length === 0 && <p className="imv-nada">Nenhuma parte lançada.</p>}
                  {partes.map((x, i) => {
                    const mudar = (parte: Partial<ParteLocal>) => setPartes((todas) => todas.map((y, k) => (k === i ? { ...y, ...parte } : y)));
                    const docRuim = !documentoConfere(x.documento);
                    return (
                      <div key={i} className="imv-parte-do-ato">
                        <div className="imv-tl imv-parte-grade">
                          <input aria-label="Nome da parte" value={x.nome} onChange={(e) => mudar({ nome: e.target.value.toLocaleUpperCase('pt-BR') })} placeholder="CICLANO DE TAL" />
                          <input
                            aria-label="CPF ou CNPJ"
                            aria-invalid={docRuim}
                            className="mono"
                            inputMode="numeric"
                            value={x.documento}
                            onChange={(e) => mudar({ documento: e.target.value })}
                            onBlur={(e) => mudar({ documento: documentoNaTela(e.target.value) })}
                            placeholder="opcional"
                          />
                          <select aria-label="Papel no ato" value={x.papel} onChange={(e) => mudar({ papel: e.target.value as PapelDaParteNoAto | '' })}>
                            <option value="">não informado</option>
                            <OpcoesAgrupadas itens={PAPEIS_DA_PARTE} atual={x.papel} />
                          </select>
                          <button type="button" className="imv-x" aria-label="Tirar esta parte" onClick={() => setPartes((todas) => todas.filter((_, k) => k !== i))}>
                            <Traco d={['M6 6l12 12', 'M18 6 6 18']} />
                          </button>
                        </div>
                        {docRuim && <p className="imv-aviso-campo">Este CPF ou CNPJ não confere pelo dígito verificador.</p>}
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="imv-mais" onClick={() => setPartes((todas) => [...todas, { nome: '', documento: '', papel: '' }])}>
                  <Traco d={['M12 5v14', 'M5 12h14']} /> Acrescentar parte
                </button>
                <p className="imv-nota">
                  O papel é o da outra parte: o alienante na compra, o banco como fiduciário. O relatório escreve a
                  frase do ato a partir dele, com o nome e o documento; o papel que não diz o que o investigado fez
                  (o cônjuge anuente, o procurador) sai numa segunda frase, com o papel de cada um.
                </p>
              </div>

              {/*
               * ⭐⭐ OS COPROPRIETÁRIOS — 16/10/2026 (PRD §51). ⛔ A fração deles não muda a
               * participação do investigado, que é a única que soma.
               */}
              <div className="imv-bloco">
                <h5 className="imv-rot">Coproprietários do mesmo bem</h5>
                <div className="imv-tabela">
                  <div className="imv-tl imv-tl-cab imv-copro-grade">
                    <span>Nome</span>
                    <span>CPF ou CNPJ</span>
                    <span>Fração (%)</span>
                    <span />
                  </div>
                  {copros.length === 0 && <p className="imv-nada">Nenhum coproprietário lançado.</p>}
                  {copros.map((x, i) => {
                    const mudar = (parte: Partial<CoproLocal>) => setCopros((todos) => todos.map((y, k) => (k === i ? { ...y, ...parte } : y)));
                    const docRuim = !documentoConfere(x.documento);
                    return (
                      <div key={i} className="imv-copro">
                        <div className="imv-tl imv-copro-grade">
                          <input aria-label="Nome do coproprietário" value={x.nome} onChange={(e) => mudar({ nome: e.target.value.toLocaleUpperCase('pt-BR') })} placeholder="BELTRANA DE TAL" />
                          <input
                            aria-label="CPF ou CNPJ do coproprietário"
                            aria-invalid={docRuim}
                            className="mono"
                            inputMode="numeric"
                            value={x.documento}
                            onChange={(e) => mudar({ documento: e.target.value })}
                            onBlur={(e) => mudar({ documento: documentoNaTela(e.target.value) })}
                            placeholder="opcional"
                          />
                          <input aria-label="Fração do coproprietário" className="mono" inputMode="decimal" value={x.fracao} onChange={(e) => mudar({ fracao: e.target.value })} placeholder="opcional" />
                          <button type="button" className="imv-x" aria-label="Tirar este coproprietário" onClick={() => setCopros((todos) => todos.filter((_, k) => k !== i))}>
                            <Traco d={['M6 6l12 12', 'M18 6 6 18']} />
                          </button>
                        </div>
                        {docRuim && <p className="imv-aviso-campo">Este CPF ou CNPJ não confere pelo dígito verificador.</p>}
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="imv-mais" onClick={() => setCopros((todos) => [...todos, { nome: '', documento: '', fracao: '' }])}>
                  <Traco d={['M12 5v14', 'M5 12h14']} /> Acrescentar coproprietário
                </button>
                {somaDasFracoes !== null ? (
                  <p className="imv-aviso-campo">
                    As frações {numero(percentual) !== null ? 'e a participação do investigado ' : ''}somam{' '}
                    {somaDasFracoes.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%: passam de 100%. Confira na
                    matrícula. O que entra na conta continua sendo só a participação do investigado.
                  </p>
                ) : (
                  <p className="imv-nota">
                    A fração de cada um não muda a participação do investigado
                    {numero(percentual) !== null ? ` (${numero(percentual)!.toLocaleString('pt-BR')}%, no passo Negócio e avaliação)` : ''}, que é a única
                    que soma.
                  </p>
                )}
              </div>

              <datalist id="imv-qualidades">
                {QUALIDADES_NO_ATO.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              <datalist id="imv-relacoes">
                {RELACOES.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </>
          )}

          {passo === 3 && (
            <>
              <h4 className="imv-passo-titulo">O negócio e a avaliação</h4>
              <p className="imv-passo-sub">
                O valor do negócio vai à tabela; a avaliação decide a soma do patrimônio. <b>Um não é o outro.</b>
              </p>
              <div className="imv-grade-campos">
                <Campo rotulo="Valor do negócio" col={3} prefixo="R$" mono lido={lido(inicial?.valorDeclarado, 'valorDeclarado')} origem={origemDe('valorDeclarado')} aoTrocar={(v) => setValorDeclarado(emCampo(Number(v)))} nota="O preço que consta do ato — não é avaliação.">
                  <input inputMode="decimal" value={valorDeclarado} onChange={(e) => setValorDeclarado(digitarReais(e.target.value))} onBlur={(e) => setValorDeclarado(completarReais(e.target.value))} placeholder="350.000,00" />
                </Campo>
                {pedeValor && (
                  <Campo
                    rotulo="Participação do investigado"
                    col={3}
                    sufixo="%"
                    mono
                    nota={
                      somaDasFracoes !== null
                        ? `Com os coproprietários, soma ${somaDasFracoes.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%: passa de 100%. Confira na matrícula.`
                        : 'Em branco não vira 100%.'
                    }
                  >
                    <input inputMode="decimal" value={percentual} onChange={(e) => setPercentual(e.target.value)} placeholder="—" />
                  </Campo>
                )}
                <div className="imv-campo c6 imv-alternar">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={valorGlobal}
                    className={`imv-op neutro${valorGlobal ? ' on' : ''}`}
                    onClick={() => setValorGlobal(!valorGlobal)}
                  >
                    <i />
                    Valor global, não individualizado
                  </button>
                  <small>Um negócio com vários bens e um preço só: o valor, se houver, é o da operação inteira.</small>
                </div>
              </div>

              {pedeValor && (
                <div className="imv-bloco">
                  <h5 className="imv-rot">Avaliações do bem</h5>
                  {origemDe('avaliacao') && <OrigemDoLido origem={origemDe('avaliacao')!} />}
                  <div className="imv-tabela">
                    <div className="imv-tl imv-tl-cab imv-av-grade">
                      <span title="entra na conta do patrimônio">Conta</span>
                      <span>Procedência</span>
                      <span>Valor</span>
                      <span>Referência</span>
                      <span>Data</span>
                      <span />
                    </div>
                    {avaliacoes.length === 0 && <p className="imv-nada">Nenhuma avaliação. Sem avaliação o bem conta sem somar dinheiro — não é zero.</p>}
                    {avaliacoes.map((a, i) => {
                      const mudar = (parte: Partial<LinhaDeAvaliacao>) =>
                        setAvaliacoes((todas) => todas.map((x, k) => (k === i ? { ...x, ...parte } : x)));
                      return (
                        <div key={a.id ?? `nova-${i}`} className={`imv-tl imv-av-grade${a.conta ? ' on' : ''}`}>
                          <input
                            type="radio"
                            aria-label="Entra na conta"
                            checked={a.conta}
                            onChange={() => setAvaliacoes((todas) => todas.map((x, k) => ({ ...x, conta: k === i })))}
                          />
                          <select aria-label="De onde veio a avaliação" value={a.modalidade} onChange={(e) => mudar({ modalidade: e.target.value as ModalidadeDaAvaliacao | '' })}>
                            <option value="">De onde veio…</option>
                            {MODALIDADES.map((m) => (
                              <option key={m.chave} value={m.chave}>
                                {m.rotulo}
                                {m.documentada ? '' : ' (derivada)'}
                              </option>
                            ))}
                          </select>
                          <input aria-label="Valor da avaliação" className="mono" inputMode="decimal" value={a.valor} onChange={(e) => mudar({ valor: digitarReais(e.target.value) })} onBlur={(e) => mudar({ valor: completarReais(e.target.value) })} placeholder="250.000,00" />
                          <input aria-label="Referência da avaliação" value={a.referencia} onChange={(e) => mudar({ referencia: e.target.value })} placeholder="R.2 da matrícula, IPTU de 2026…" />
                          <input aria-label="Data da avaliação" type="date" value={a.avaliadaEm} onChange={(e) => mudar({ avaliadaEm: e.target.value })} />
                          <button type="button" className="imv-x" aria-label="Tirar esta avaliação" onClick={() => setAvaliacoes((todas) => todas.filter((_, k) => k !== i))}>
                            <Traco d={['M6 6l12 12', 'M18 6 6 18']} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="imv-mais"
                    onClick={() =>
                      setAvaliacoes((todas) => [...todas, { modalidade: '', valor: '', referencia: '', avaliadaEm: '', conta: todas.every((x) => !x.conta) }])
                    }
                  >
                    <Traco d={['M12 5v14', 'M5 12h14']} /> Acrescentar avaliação
                  </button>
                  <p className="imv-nota">
                    Do bem integral. Judicial, laudo e valor declarado avaliam <b>este</b> bem; as demais são{' '}
                    <b>derivadas</b> — valem, e o relatório dirá que são. <b>A apurar</b> não é zero.
                  </p>
                </div>
              )}
            </>
          )}

          {passo === 4 && (
            <>
              <h4 className="imv-passo-titulo">Gravames e certidão</h4>
              <p className="imv-passo-sub">
                Só gravame <b>ativo</b> soma. Valor em branco é não quantificado na matrícula, nunca zero.
              </p>
              {corrigindo ? (
                <div className="imv-bloco">
                  <div className="imv-tabela">
                    <div className="imv-tl imv-tl-cab imv-gr-grade imv-gr-acoes">
                      <span>Natureza</span>
                      <span>Credor ou órgão</span>
                      <span>Processo</span>
                      <span>Valor</span>
                      <span>Desde</span>
                      <span>Situação</span>
                      <span />
                    </div>
                    {onusExistentes.length === 0 && <p className="imv-nada">Nenhum gravame lançado.</p>}
                    {onusExistentes.map((o) => {
                      if (gravameEmEdicao?.id === o.id) {
                        const g = gravameEmEdicao;
                        const mudar = (parte: Partial<GravameLocal>) => setGravameEmEdicao({ ...g, ...parte });
                        return (
                          <div key={o.id} className="imv-tl imv-gr-grade imv-gr-acoes">
                            <input aria-label="Natureza" value={g.natureza} onChange={(e) => mudar({ natureza: e.target.value })} />
                            <input aria-label="Credor ou órgão" value={g.credor} onChange={(e) => mudar({ credor: e.target.value })} placeholder="Banco · juízo" />
                            <input aria-label="Processo" className="mono" value={g.processo} onChange={(e) => mudar({ processo: e.target.value })} placeholder="nº do processo" />
                            <input aria-label="Valor" className="mono" inputMode="decimal" value={g.valor} onChange={(e) => mudar({ valor: digitarReais(e.target.value) })} onBlur={(e) => mudar({ valor: completarReais(e.target.value) })} placeholder="—" />
                            <input aria-label="Constituído em" type="date" value={g.constituidoEm} onChange={(e) => mudar({ constituidoEm: e.target.value })} />
                            <select aria-label="Situação" value={g.situacao} onChange={(e) => mudar({ situacao: e.target.value })}>
                              <option value="">não apurada</option>
                              <option value="ATIVO">ativo</option>
                              <option value="CANCELADO">cancelado</option>
                              <option value="QUITADO SEM BAIXA">quitado sem baixa</option>
                              {g.situacao && !['ATIVO', 'CANCELADO', 'QUITADO SEM BAIXA'].includes(g.situacao) && (
                                <option value={g.situacao}>{g.situacao.toLocaleLowerCase('pt-BR')}</option>
                              )}
                            </select>
                            <span className="imv-acoes-linha">
                              <button type="button" className="imv-x" aria-label="Gravar a correção deste gravame" disabled={gravandoGravame} onClick={() => void gravarGravame()}>
                                <Traco d={['M5 12l5 5L19 7']} />
                              </button>
                              <button type="button" className="imv-x" aria-label="Desistir da correção" onClick={() => setGravameEmEdicao(null)}>
                                <Traco d={['M6 6l12 12', 'M18 6 6 18']} />
                              </button>
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div key={o.id} className="imv-tl imv-gr-grade imv-gr-acoes">
                          <span>{o.natureza}</span>
                          <span>{o.credor ?? '—'}</span>
                          <span className="mono">{o.processo ?? '—'}</span>
                          <span className="mono">{o.valor !== null ? dinheiro(o.valor) : '—'}</span>
                          <span className="mono">{emData(o.constituidoEm) || '—'}</span>
                          <span className={`imv-mini-sit${o.situacao ? '' : ' na'}`}>{o.situacao ?? 'não apurada'}</span>
                          {ehMaster ? (
                            <span className="imv-acoes-linha">
                              <button
                                type="button"
                                className="imv-x"
                                aria-label="Editar este gravame"
                                title="Editar"
                                disabled={gravameEmEdicao !== null}
                                onClick={() =>
                                  setGravameEmEdicao({
                                    id: o.id,
                                    natureza: o.natureza,
                                    credor: o.credor ?? '',
                                    processo: o.processo ?? '',
                                    valor: emCampo(o.valor),
                                    constituidoEm: o.constituidoEm ?? '',
                                    situacao: o.situacao ?? '',
                                  })
                                }
                              >
                                <Traco d={['M4 20h4L19 9l-4-4L4 16v4', 'M14 6l4 4']} />
                              </button>
                              <button
                                type="button"
                                className="imv-x"
                                aria-label="Apagar este gravame"
                                title="Apagar"
                                onClick={() => setGravameAApagar(o)}
                              >
                                <Traco d={['M6 6l12 12', 'M18 6 6 18']} />
                              </button>
                            </span>
                          ) : (
                            <span />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {ehMaster && (
                    <FormularioDeOnus
                      ativo={{ ...corrigindo, onus: onusExistentes }}
                      investigadoId={investigado.id}
                      comProcesso
                      aoLancar={() =>
                        void apiInvestigados
                          .ativos(investigado.id)
                          .then((r) => setOnusExistentes(r.ativos.find((x) => x.id === corrigindo.id)?.onus ?? onusExistentes))
                      }
                    />
                  )}
                  <p className="imv-nota">Gravame lançado, corrigido ou apagado aqui grava na hora.</p>
                </div>
              ) : (
                <div className="imv-bloco">
                  <div className="imv-tabela">
                    <div className="imv-tl imv-tl-cab imv-gr-grade">
                      <span>Natureza</span>
                      <span>Credor ou órgão</span>
                      <span>Processo</span>
                      <span>Valor</span>
                      <span>Desde</span>
                      <span>Situação</span>
                      <span />
                    </div>
                    {gravames.length === 0 && <p className="imv-nada">Nenhum gravame.</p>}
                    {gravames.map((g, i) => {
                      const mudar = (parte: Partial<GravameLocal>) =>
                        setGravames((todos) => todos.map((x, k) => (k === i ? { ...x, ...parte } : x)));
                      return (
                        <div key={i} className="imv-tl imv-gr-grade">
                          <input aria-label="Natureza" list="imv-naturezas" value={g.natureza} onChange={(e) => mudar({ natureza: e.target.value })} placeholder="Alienação fiduciária" />
                          <input aria-label="Credor ou órgão" value={g.credor} onChange={(e) => mudar({ credor: e.target.value })} placeholder="Banco · juízo" />
                          <input aria-label="Processo" className="mono" value={g.processo} onChange={(e) => mudar({ processo: e.target.value })} placeholder="nº do processo" />
                          <input aria-label="Valor" className="mono" inputMode="decimal" value={g.valor} onChange={(e) => mudar({ valor: digitarReais(e.target.value) })} onBlur={(e) => mudar({ valor: completarReais(e.target.value) })} placeholder="—" />
                          <input aria-label="Constituído em" type="date" value={g.constituidoEm} onChange={(e) => mudar({ constituidoEm: e.target.value })} />
                          <select aria-label="Situação" value={g.situacao} onChange={(e) => mudar({ situacao: e.target.value })}>
                            <option value="">não apurada</option>
                            <option value="ATIVO">ativo</option>
                            <option value="CANCELADO">cancelado</option>
                            <option value="QUITADO SEM BAIXA">quitado sem baixa</option>
                          </select>
                          <button type="button" className="imv-x" aria-label="Tirar este gravame" onClick={() => setGravames((todos) => todos.filter((_, k) => k !== i))}>
                            <Traco d={['M6 6l12 12', 'M18 6 6 18']} />
                          </button>
                          {/* ⭐ R4: de onde o gravame saiu, logo embaixo da linha. */}
                          {g.origem && <OrigemDoLido origem={g.origem} linha />}
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="imv-mais"
                    onClick={() => setGravames((todos) => [...todos, { natureza: '', credor: '', processo: '', valor: '', constituidoEm: '', situacao: '' }])}
                  >
                    <Traco d={['M12 5v14', 'M5 12h14']} /> Acrescentar gravame
                  </button>
                  <datalist id="imv-naturezas">
                    {['Alienação fiduciária', 'Hipoteca', 'Penhora', 'Arresto', 'Sequestro', 'Indisponibilidade', 'Usufruto', 'Promessa registrada', 'Cessão fiduciária', 'Averbação premonitória', 'Citação de ação real'].map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                  <p className="imv-nota">⚠️ Na penhora, o que a averbação nomeia costuma ser o juízo, não o credor.</p>
                </div>
              )}
              <div className="imv-grade-campos">
                <Campo rotulo="Data da certidão" col={3} mono lido={lido(inicial?.certidaoEm, 'certidaoEm')} origem={origemDe('certidaoEm')} nota="De quando é o retrato registral — não a data da aquisição. Passados 90 dias, o Hub avisa.">
                  <input type="date" value={certidaoEm} onChange={(e) => setCertidaoEm(e.target.value)} />
                </Campo>
              </div>
            </>
          )}

          {passo === 5 && (
            <>
              <h4 className="imv-passo-titulo">Conferir antes de {corrigindo ? 'gravar' : 'lançar'}</h4>
              <p className="imv-passo-sub">É assim que o imóvel sai no relatório. Clique num passo acima para ajustar.</p>
              <Previa p={previa} />
              {pendencias.length > 0 && (
                <p className="imv-pendencias">
                  Ainda em branco: <b>{pendencias.join(' · ')}</b>. Pode {corrigindo ? 'gravar' : 'lançar'} assim e completar
                  depois — em branco sai "a preencher", nunca inventado.
                </p>
              )}
            </>
          )}
        </div>

        {passo < 2 && <Ajuda passo={passo} />}
      </div>

      {erro && <p className="erro imv-erro">{erro}</p>}
      {!corrigindo && bloqueio && passo === PASSOS.length - 1 && <p className="ajuda-alerta imv-erro">{bloqueio}</p>}
      {/* ⭐ §77: a caixinha da parte, ao lado do "Lançar" (o papel não traz o documento nem o nome do investigado) */}
      {confirmacao && passo === PASSOS.length - 1 && (
        <label className="imv-confirma-parte">
          <input type="checkbox" checked={confirmacao.feita} onChange={(e) => confirmacao.aoMudar(e.target.checked)} /> {confirmacao.texto}
        </label>
      )}

      <footer className="imv-ficha-pe">
        {passo > 0 ? (
          <button type="button" className="imv-bt fantasma" onClick={() => irPara(passo - 1)}>
            <Traco d={['m15 6-6 6 6 6']} /> Voltar
          </button>
        ) : (
          <span />
        )}
        <span className="imv-esp" />
        <span className="imv-prog">
          <span className="imv-prog-barra">
            <i style={{ width: `${((passo + 1) / PASSOS.length) * 100}%` }} />
          </span>
          passo {passo + 1} de {PASSOS.length}
        </span>
        <button type="button" className="imv-bt fantasma" onClick={aoDescartar ?? aoFechar}>
          {aoDescartar ? 'Descartar' : 'Cancelar'}
        </button>
        {passo < PASSOS.length - 1 ? (
          <button type="button" className="imv-bt primario" onClick={() => irPara(passo + 1)}>
            Avançar <Traco d={['m9 6 6 6-6 6']} />
          </button>
        ) : (
          <button
            type="button"
            className="imv-bt primario"
            disabled={gravando || (!corrigindo && !!bloqueio) || (!!confirmacao && !confirmacao.feita)}
            title={!corrigindo && bloqueio ? bloqueio : undefined}
            onClick={() => void gravar()}
          >
            <Traco d={['M5 12.5 10 17 19 7.5']} />
            {gravando ? 'Gravando…' : corrigindo ? 'Gravar a correção' : 'Lançar imóvel'}
          </button>
        )}
      </footer>
      {gravameAApagar && corrigindo && (
        <Confirmar
          titulo="Apagar este gravame?"
          descricao={`${gravameAApagar.natureza}${gravameAApagar.credor ? ` · ${gravameAApagar.credor}` : ''} sai deste imóvel agora, e não volta.`}
          aoConfirmar={() =>
            void apiInvestigados
              .apagarOnus(investigado.id, corrigindo.id, gravameAApagar.id)
              .then(() => setOnusExistentes((l) => l.filter((x) => x.id !== gravameAApagar.id)))
              .catch(avisarErro)
          }
          aoFechar={() => setGravameAApagar(null)}
        />
      )}
    </section>
  );
}

function Campo({
  rotulo,
  obrigatorio,
  col,
  lido,
  mono,
  prefixo,
  sufixo,
  nota,
  origem,
  aoTrocar,
  porque,
  children,
}: {
  rotulo: string;
  obrigatorio?: boolean;
  col: 1 | 2 | 3 | 4 | 6;
  lido?: string;
  mono?: boolean;
  prefixo?: string;
  sufixo?: string;
  nota?: string;
  /** ⭐ R4: de onde o valor lido veio, e o motivo de conferir. */
  origem?: OrigemDoCampo;
  /** ⭐ §77: põe no campo a outra leitura (quando o Hub e o GPT divergiram). */
  aoTrocar?: (valor: string | number) => void;
  /** ⭐ §78: a origem é um motivo (o Hub decidiu pelo caso), e não um trecho do papel. */
  porque?: boolean;
  children: React.ReactNode;
}) {
  const dentro = (
    <>
      <span className="imv-rotulo">
        {rotulo}
        {obrigatorio && <i className="imv-req" title="obrigatório" />}
      </span>
      <span className={`imv-in${mono ? ' mono' : ''}${lido ?? ''}${lido && origem?.conferir ? ' conferir' : ''}`}>
        {prefixo && <em className="imv-pre">{prefixo}</em>}
        {children}
        {sufixo && <em className="imv-suf">{sufixo}</em>}
      </span>
      {nota && <small>{nota}</small>}
    </>
  );
  /* ⚠️ A origem fica FORA do rótulo: dentro dele, o trecho do papel viraria parte do nome do campo para quem usa
     leitor de tela. */
  if (!origem || !lido) return <label className={`imv-campo c${col}`}>{dentro}</label>;
  return (
    <div className={`imv-campo c${col}`}>
      <label className="imv-campo-dentro">{dentro}</label>
      <OrigemDoLido origem={origem} aoTrocar={aoTrocar} porque={porque} />
    </div>
  );
}

/**
 * ⭐⭐ **DE ONDE VEIO — R4 da reforma das leituras, 27/10/2026 (PRD §67.5).** O motivo de conferir fica à vista, em
 * amarelo; a página, o ato e o trecho do papel ficam a um toque (não por cima do mouse: no celular não há mouse).
 */
function OrigemDoLido({
  origem,
  porque,
  linha,
  aoTrocar,
}: {
  origem: OrigemDoCampo;
  porque?: boolean;
  linha?: boolean;
  aoTrocar?: (valor: string | number) => void;
}) {
  const onde = [origem.pagina !== null ? `pág. ${origem.pagina}` : null, origem.ato].filter(Boolean).join(' · ');
  /* ⭐ §77: a outra leitura, a um clique; depois de trocar, o botão diz qual está no campo */
  const [trocado, setTrocado] = useState(false);
  return (
    <div className={`imv-origem${origem.conferir ? ' conferir' : ''}${linha ? ' na-linha' : ''}`}>
      {origem.conferir && (
        <p className="imv-conferir">
          <b>Conferir:</b> {origem.conferir}
        </p>
      )}
      {origem.outra && aoTrocar && (
        <p className="imv-outra-leitura">
          {trocado ? (
            <>Está no campo a leitura {origem.outra.de === 'gpt' ? 'do GPT' : 'do Hub'}.</>
          ) : (
            <>
              {/* com o amarelo, as duas leituras já estão ditas: fica só o botão */}
              {!origem.conferir && (
                <>
                  {origem.outra.de === 'gpt' ? 'O GPT' : 'O Hub'} leu {origem.outra.naTela}.{' '}
                </>
              )}
              <button
                type="button"
                className="imv-bt p fantasma"
                onClick={() => {
                  aoTrocar(origem.outra!.valor);
                  setTrocado(true);
                }}
              >
                {origem.conferir ? `Usar a ${origem.outra.de === 'gpt' ? 'do GPT' : 'do Hub'}` : 'Usar esta'}
              </button>
            </>
          )}
        </p>
      )}
      {!origem.conferir && !origem.outra && origem.quem && origem.quem !== 'os-dois' && (
        <p className="imv-quem-leu">Lido só {origem.quem === 'gpt' ? 'pelo GPT' : 'pelo Hub'}.</p>
      )}
      {origem.trecho &&
        (porque ? (
          <p className="imv-porque">
            <b>Por que esta sugestão:</b> {origem.trecho}
          </p>
        ) : (
          <details>
            <summary>De onde veio{onde ? ` · ${onde}` : ''}</summary>
            <q>{origem.trecho}</q>
          </details>
        ))}
    </div>
  );
}

const AJUDAS: Array<{ titulo: string; texto: React.ReactNode }> = [
  {
    titulo: 'Qual situação escolher?',
    texto: (
      <>
        <p>
          <b>Livre</b> e <b>onerado</b> estão no nome do investigado e somam. <b>Transmitido</b> saiu do nome dele;{' '}
          <b>matrícula encerrada</b> seguiu em outra; <b>em nome de terceiro</b> nunca foi dele — os três não somam.
        </p>
        <p>
          <b>Livre, empresa do grupo</b> soma quando a empresa titular é parte do caso; quando não é, só demonstra o
          vínculo.
        </p>
        <p>O endereço sai como manchete da ficha no relatório: escreva como a matrícula descreve.</p>
      </>
    ),
  },
  {
    titulo: 'Qual ato lançar?',
    texto: (
      <>
        <p>
          <b>Sempre o último ato em que o investigado participou</b> — compra, venda, doação, garantia —, mesmo que o
          bem já não seja dele.
        </p>
        <p>
          Imóvel doado ao filho, que vendeu a outro: lança-se a <b>doação</b>, e só ela.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quem são as partes?',
    texto: (
      <>
        <p>
          Quem esteve do outro lado <b>deste ato</b>: o vendedor na compra, o donatário na doação, o banco na
          alienação fiduciária. Cada uma com o papel dela: é dele que sai a frase do relatório.
        </p>
        <p>
          <b>Coproprietário</b> é quem divide o bem com o investigado. A fração dele não muda a do investigado.
        </p>
        <p>A relação só se escreve quando o documento a demonstra.</p>
      </>
    ),
  },
  {
    titulo: 'Preço não é avaliação',
    texto: (
      <>
        <p>
          O <b>valor do negócio</b> é o que o ato declara. A <b>avaliação</b> é quanto o bem vale — e é ela que soma.
        </p>
        <p>Marque uma avaliação como a que entra na conta. Sem nenhuma, o bem conta sem somar dinheiro.</p>
      </>
    ),
  },
  {
    titulo: 'Gravame e certidão',
    texto: (
      <>
        <p>
          O valor do gravame é o do ato registrado, <b>não o saldo devedor</b> — e nunca se subtrai do patrimônio.
        </p>
        <p>A data da certidão diz de quando é o retrato: penhora posterior não aparece nela.</p>
      </>
    ),
  },
];

function Ajuda({ passo, faixa }: { passo: number; faixa?: boolean }) {
  const a = AJUDAS[passo];
  if (!a) return null;
  return (
    <aside className={`imv-ajuda${faixa ? ' faixa' : ''}`}>
      <span className="imv-rot ouro">{a.titulo}</span>
      {a.texto}
    </aside>
  );
}

/**
 * ⭐ **A prévia da ficha, como sai no relatório — 16/09/2026.** O mesmo
 * desenho aprovado para o PDF (P1): número, o bem como manchete, a matrícula,
 * o selo da classificação, e três blocos. Em branco sai "a preencher".
 */
function Previa({
  p,
}: {
  p: {
    investigado: string;
    qualidade: string;
    data: string;
    titulo: string;
    origem: string;
    outraParte: string;
    /** ⭐ 16/10/2026: a frase do ato, os coproprietários e a empresa titular. */
    frase: string | null;
    coproprietarios: string;
    empresaTitular: string;
    relacao: string;
    objeto: string;
    matricula: string;
    cartorio: string;
    cidadeUf: string;
    valor: string;
    valorGlobal: boolean;
    situacao: string;
    classificacao: ClassificacaoDoAtivo;
  };
}) {
  /* ⭐ PRD §79 (30/10/2026): como no relatório, a ficha toda em letra maiúscula (o que foi digitado fica como está) */
  const M = (t: string) => t.toLocaleUpperCase('pt-BR');
  const FALTA = <p className="falta">A PREENCHER</p>;
  const v = (t: string, mono = false) => (t.trim() ? <p className={mono ? 'mono' : undefined}>{M(t)}</p> : FALTA);
  const valor = p.valor ? `${p.valor}${p.valorGlobal ? ' — valor global da operação' : ''}` : p.valorGlobal ? 'Valor global não individualizado' : '';
  return (
    <div className="imv-previa">
      <div className="imv-previa-topo">
        <span className="led" />
        <span className="imv-rot">Como sai no relatório</span>
        <small>cor do nível do caso</small>
      </div>
      <div className="imv-pv">
        <div className="imv-pv-num">
          <b>01</b>
          <span>imóvel</span>
        </div>
        <div className="imv-pv-cab">
          <p className="imv-pv-objeto">{M(p.objeto || 'a preencher')}</p>
          <p className="imv-pv-mat">
            MATRÍCULA <span className="mono">{p.matricula || '—'}</span>
            {p.cartorio && <> · {M(p.cartorio)}</>}
            {p.cidadeUf && <> · {M(p.cidadeUf)}</>}
          </p>
        </div>
        <div className="imv-pv-selo">
          <i>Classificação</i>
          <span className={`imv-pv-classe ${CLASSE_CSS[p.classificacao]}`}>{ROTULO[p.classificacao]}</span>
        </div>
        <div className="imv-pv-bloco">
          <h6>O ato</h6>
          <i>Data</i>
          {v(p.data, true)}
          <i>Escritura/título</i>
          {v(p.titulo)}
          <i>Tabelionato/origem</i>
          {v(p.origem)}
        </div>
        <div className="imv-pv-bloco">
          <h6>As partes</h6>
          <i>Investigado</i>
          <p className="forte">{M(p.investigado)}</p>
          <i>Qualidade</i>
          {v(p.qualidade)}
          <i>Outra(s) parte(s)</i>
          {p.outraParte.trim() ? <p className="forte">{M(p.outraParte)}</p> : FALTA}
          <i>Relação</i>
          {v(p.relacao)}
        </div>
        <div className="imv-pv-bloco">
          <h6>O negócio</h6>
          <i>Valor</i>
          {v(valor, Boolean(p.valor))}
          <i>Situação patrimonial</i>
          {p.situacao ? <p className="forte cor">{M(p.situacao)}</p> : FALTA}
        </div>
        {/* ⭐ 16/10/2026 (PRD §51): o pé da ficha, como sai no relatório. */}
        {(p.frase || p.coproprietarios || p.empresaTitular) && (
          <div className="imv-pv-bloco imv-pv-pe">
            {p.frase && (
              <>
                <i>Partes do ato</i>
                <p>{M(p.frase)}</p>
              </>
            )}
            {p.coproprietarios && (
              <>
                <i>Coproprietários</i>
                <p>{M(p.coproprietarios)}</p>
              </>
            )}
            {p.empresaTitular && (
              <>
                <i>Empresa titular</i>
                <p>{M(p.empresaTitular)}</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
